<template>
   <!-- HISTORY:
   V260520.1: Implemented formatChartData() to handle range values of decimal fields in the histogram model + Added Today and Yesterday to datePickerItems.
   V260518.1: Changed histogram filter operators in getSelectColumnsAndData(): replaced '>' with '>=' for '>max', and 'BETWEEN min and max' with '>= min AND < max' for 'min-max' range comparisons.
   V260512.1: Consumed the new BtIdResolutionDefiner component for dropdowns and charts as well.
   V260511.1: Consumed the new BtIdResolutionDefiner component to obtain display names for IDs where appropriate, and passed the data to BtCalculatedTable's idResolutionsData prop.
   V260501.1: Fixed a bug related to the sortColumn dropdown by adding optional chaining (?.) for model and metrics.
   V260402.1: Added supported for sortColumn and sortOrder in calculated tables.
   V260320.1: Refactored label logic: Replaced chartsSettings prop with dashboardsLabels and implemented a new effectiveLabels var to handle label prioritization. Additionally, ensured table headers in PaginatedTable correctly resolve these labels.
   V260318.2: Removed charts-settings prop from BtCalculatedTable +
      Enabled optional calculated columns for Pie Charts and metrics models, and added metric selection support (metricsVisibility, visibleMetrics).
   V260313.1: Added filter and exclude properties to the chart's chartClickData.
   V260305.2: Consumed the new BtKpiForLS component and made calculated columns available to KPIs +
      Renamed ctCalculatedColumns, ctColumnsSequence, ctSortColumn, ctSortOrder, and ctErrMsg.
   V260226.2: Implemented caching logic (using an MD5 hashing mechanism) and integrated it into getChartData(), getPivotDimInItems(), getPaginatedTableCount(), and part of getPaginatedTableData().
   V260224.1: For CalculatedTable, used loadingChartData instead of loadingCalculatedData, and handled the ready event for the same purposes.
   V260223.1: Implemented new business logic to handle cell clicks (instead of row click) in CalculatedTable +
      Formatted <v-data-table> pageText (total records).
   V260128.3: Made chartLabels available for the dims-metrics model and supported click event + Fixed an issue where chart click didn't work for day/month(field) +
      Supported click events for date(DATE | TIMESTAMP) cases.
   V260127.1: Enhanced <v-card> chart loading behavior to turn off when charts are ready.
   V260114.1: Fixed a bug where GeoCharts were not detected when preparing data. Modified GeoChart eligibility by removing GEO_HIERARCHY validation, and updated chartTypeChanged() to set options.displayMode to render locations as circles.
   V260108.1: Added chartLabels field for charts using *-dim models and event_code to map event codes to labels.
   V260105.1: Made title available for dropdown global filters as well (by duplication title field in the UI).
   V251205.1: [not sent to RF] In configureChart, Removed colors from options for visualization purposes + Added Show/Hide Sliced Data btn in v-card-actions.
   V251204.1: Added CalculatedTable + Made sliced data available to the admin users only.
   V251124.1: Reset PaginatedTable page number before getting data.
   V251120.1: Added PaginatedTable w/out Create Export feature & options + Add table/chart toggle btn for charts + Removed apiService prop and added cs/lsApiService & btHelpers instead +
      Removed global colors from charts dropdowns.
   V251104.1: Supported chart-click events and global chart colors + Fixed DonutChart chart type + Disabled filter change and chart-click in edit mode +
      Fixed 2-way binding bug and issues that didn't enable/disable save buttons.
   V251027.2: 1st release.
   -->
   <!-- TODO:
   - 05/13/26: Disable model for PaginatedTable as it only accepts whereClause and it shouldn't be filled. Otherwise, click events cannot filter contacts properly.
   - 03/24/26: In KPI dashboard, 1st Pie chart doesn't show Other label for 0 where as the other 2 shows.
   - Pass clearable prop in BtCalculatedColumns for all charts and KPI but the Calculated Table.
   - Create isCalculated, isPaginated, and isKpi computed vars.
   - Implement Create Export
   - Is ptSettings box required?
   - Get rid of queryResult in template and set it to chartData + combine modelDims & modelLabels with dimKeys & metricKeys
   - Squeeze v-data-table to make it shorter or create a custom one,
   - Remove the scrolling in chart configuration page
   - Separate filters template from charts even some of the fields will be duplicate. Yet, the logic will be easier
   - IMPORTANT: Changing the model in a way that changes chart types (and selected is not available anymore), is not detected.
      In other words, if chartType not selected, doesn't give error. PERHAPS, AFTER MODEL CHANGE, VERIFY CHARTiTEMS AND REMOVE V-MODEL IF DOESN'T EXIST
   - IMPORTANT: in value watch, check newVal and oldVal to see if init should be called or not.
   -->
   <!--BUSINESS LOGIC:
   - PaginatedTable:
      . Call metrics model with count on _id (or distinct) to get the total
      . Call column-distinct model with column='_id' and limit & offset to get IDs.
      . Call ListService (same as PaginatedTable) to get the records
      . Ask for the columns to query
   - CalculatedTable: Add a switch then show a model with dims-dim. Then 2nd mode (optional) with dim-metrics
   -->
   <div>
      <v-alert v-if="errorMessage" dense outlined class="mb-0"
         type="error"
      >{{errorMessage}}</v-alert>
      
       <v-card flat
         :loading="loadingChartData"
      >
         <v-card-title class="title grey--text darken-4 font-weight-bold pl-3 pr-0 pt-2 pb-0 pdf-flex">
            <v-icon v-if="chart.icon">{{chart.icon}}</v-icon>
            <span class="pl-1">{{chart.title}}</span>
            <div class="flex-grow-1"></div>
            <v-btn v-if="chart.chartType === 'CalculatedTable'"
               x-small
               class="mr-3"
               @click="exportCalculatedTableClicked"
            >Export Data to CSV
               <v-icon right dark>file_upload</v-icon>
            </v-btn>
            <!-- <v-btn v-if="chart.chartType === 'PaginatedTable' && canCreateExport"
               x-small
               class="mr-3"
               @click="createExportClicked"
            >Create Export
               <v-icon right dark>file_upload</v-icon>
            </v-btn> -->
            <v-tooltip left v-if="isEditing">
               <template v-slot:activator="{ on }">
                  <v-btn text icon
                     v-on="on"
                     @click="configureChart"
                  >
                     <v-icon>more_vert</v-icon>
                  </v-btn>
               </template>
               <span>{{btnTooltip}}</span>
            </v-tooltip>
            <v-icon v-else-if="canToggle"
               class="pr-2"
               @click="switchBetweenChartAndTable"
            >
               {{isTable ? 'bar_chart' : 'table_chart'}}
            </v-icon>
         </v-card-title>

         <v-card-text v-if="chart.filterType === 'DatePicker'"
            :class="vCardTextClass"
         >
            <v-layout>
               <v-card-text flat
                  class="px-0 py-0"
               >
                  <bt-date-picker
                     :debug="debug"
                     :dd-icon="`${chart.icon ? chart.icon : 'date_range'}`"
                     :dd-custom-label="true"
                     dd-label="select a date range..."
                     :dd-monthly-items="datePickerItems"
                     :dd-value="chart.defaultId"
                     :dp-no-title="false"
                     :dp-max-range="parseInt(chart.maxRange)"
                     :disabled="isEditing"
                     v-model="datePickerRange"
                     @date-change="dateChanged"
                  ></bt-date-picker>
               </v-card-text>
            </v-layout>
         </v-card-text>

         <v-card-text v-else-if="chart.filterType === 'Dropdown'"
            :class="vCardTextClass"
         >
               <!-- :preselected-items="chart.lastDropdownSelections" -->
            <bt-autocomplete chips persistent-hint small-chips single-line
               excluded-label="Exclude"
               included-label="Include"
               prepend-inner-icon="filter_list"
               :clearable="!chart.autoSelectFirstItem"
               :counter="chartData.length"
               :deletable-chips="!chart.autoSelectFirstItem"
               :disabled="isEditing"
               :is-included="true"
               :items="chartData"
               :label="chart.title"
               :multiple="chart.multiSelect"
               :preselected-items="chart.lastValues"
               :return-object="false"
               @switch-changed="dropdownSwitchChanged"
               @change="dropdownSelectionsChanged"
            ></bt-autocomplete>
         </v-card-text>

         <v-card-text v-else-if="chart.chartType === 'Table'"
            class="px-3 py-0 pb-2"
         >
            <v-data-table dense fixed-header
               class="elevation-1"
               :footer-props="{
                  itemsPerPageOptions: [5, 10, 20, -1],
                  showFirstLastPage: true
               }"
               :headers="queryResultHeaders"
               :hide-default-footer="queryResult.length <= 5"
               :items="queryResult"
               :items-per-page="5"
               :loading-text="$t('loading-text')"
               :no-data-text="$t('no-data-text', { value: 'results' })"
               :no-results-text="$t('no-results-text', { value: 'results' })"
            >
               <template v-slot:item="{item, headers, index}">
                  <tr
                     :key="index"
                     :class="{ 'clicked-row': clickedRowInd === index }"
                     @click="tableRowClicked(item, index, chart.model.dims)"
                  >
                     <td v-for="header in headers" :key="header.value">
                        {{ formatNumber(item[header.value], header.value) }}
                     </td>
                  </tr>
               </template>
            </v-data-table>
         </v-card-text>

         <v-card-text v-else-if="chart.chartType === 'PaginatedTable'"
            class="px-3 py-0 pb-2"
         >
            <v-data-table dense show-expand single-expand
               class="elevation-1 mb-2"
               :footer-props="{
                  itemsPerPageOptions:[5, 10, 20],
                  showFirstLastPage: true
               }"
               :headers="ptHeaders"
               :hide-default-footer="!ptItemsCount"
               item-key="_id"
               :items="chartData"
               :items-per-page="5"
               :loading="ptLoading"
               :loading-text="$t('loading-text')"
               :no-data-text="$t('no-data-text', { value: 'data' })"
               :no-results-text="$t('no-results-text', { value: 'data' })"
               :options.sync="ptOptions"
               :server-items-length="ptItemsCount"
               @update:expanded="ptItemExpanded"
            >
               <template v-slot:[`item.actions`]="{ item }">
                  <div v-if="item._mfi_hasContactProfile"
                     @click="contactProfileClicked(item)"
                  >
                        <!-- :labels="chartsSettings?.labels" -->
                     <bt-contact-profile-for-cs
                        :contact-data="item.contactData"
                        :event-data-fields="eventDataFields"
                        :labels="effectiveLabels"
                        :kpis-data="cpKpisData"
                        :monthly-chart-data="monthlyChartData"
                        :weekly-chart-data="weeklyChartData"
                        :settings="chart.ptSettings || {}"
                        :debug="debug"
                     ></bt-contact-profile-for-cs>
                  </div>
               </template>
               <!-- V260223 -->
               <template v-slot:[`footer.page-text`]="{ pageStart, pageStop, itemsLength }">
                  {{ pageStart }}-{{ pageStop }} of {{ itemsLength.toLocaleString() }}
               </template>
               <template v-slot:expanded-item="{ item }">
                  <td v-for="(colspan, i) in ptColspan" :key="i"
                     dense
                     class="py-2"
                     valign="top"
                     :colspan="colspan"
                  >
                     <ul v-if="item.details">
                        <li v-for="(detail, j) in item.details[i]" :key="`${i}_${j}`">
                           <span class="expanded-header">{{detail.key}}: </span>
                           <span v-if="detail.key==='events'"
                              class="expanded-content"
                           >
                              <v-btn v-if="detail.value.length"
                                 text icon dense
                                 @click="eventsClicked(detail.value)"
                              >{{detail.value.length}}
                                 <v-icon>more_horiz</v-icon>
                              </v-btn>
                              <span v-else>None</span>
                           </span>
                           <span v-else
                              class="expanded-content"
                           >{{detail.value}}
                           </span>
                        </li>
                     </ul>
                  </td>
               </template>
            </v-data-table>
         </v-card-text>

         <v-card-text v-else-if="chart.chartType === 'CalculatedTable'"
            class="px-3 pt-1 pb-2"
         >
            <!-- :charts-settings="chartsSettings" -->
            <bt-calculated-table
               :calculated-columns="chart.calculatedColumns"
               :clicked-row-index="clickedRowInd"
               :columns-sequence="chart.ctColumnsSequence ? chart.ctColumnsSequence.split(',') : []"
               :original-data="chartData"
               :title="chart.title"
               :debug="debug"
               :export-data="exportCalculatedTable"
               :id-resolutions-data="idResolutionsData"
               :is-editing="isEditing"
               :labels="effectiveLabels"
               :loading="loadingChartData"
               :model-dims="modelDims"
               :model-labels="modelLabels"
               :sort-column="chart.ctSortColumn || chart.sortColumn"
               :sort-order="chart.ctSortOrder || chart.sortOrder"
               @ready="ctReady"
               @click="ctCellClicked"
               @reclick="ctCellReclicked"
               @export="ctExported"
            ></bt-calculated-table>
         </v-card-text>

         <v-card-text v-else-if="chart.chartType === 'KPI'"
            outlined
            class="pa-3"
         >
            <!--V260305: <v-row dense
               v-for="(value, key) in queryResult[0]"
               :key="key"
               class="body-1 font-weight-bold"
            >
               <v-col cols="12"><span class="grey--text text-darken-1">{{ key }}: </span>{{ formatNumber(value) }}</v-col>
            </v-row> -->
            <bt-kpi
               :calculated-columns="chart.calculatedColumns"
               :debug="debug"
               :is-editing="isEditing"
               :loading="loadingChartData"
               :model="chart.model"
               :original-data="queryResult?.[0] || {}"
               :visible-metrics="getVisibleMetrics()"
               @ready="loadingChartData = false"
               @click="ctCellClicked"
               @reclick="kpiMetricReclicked"
            />
         </v-card-text>

         <v-card-text v-else-if="chart.chartType"
            class="px-3 py-0 pb-2"
         >
            <!-- TODO: change v-else to v-hide for the console error -->
            <GChart
               ref="gChart"
               :data="chartData"
               :type="chart.chartType === 'DonutChart' ? 'PieChart' : chart.chartType"
               :options="chart.options"
               :settings="gChartSettings"
               :events="chartEvents"
               :key="colSize"
            />
         </v-card-text>
      </v-card>

      <v-dialog no-click-animation persistent scrollable
         max-width="840px"
         v-model="dialogChartConfig"
      >
         <v-card>
            <v-card-title class="title grey--text darken-4 font-weight-bold py-3">Chart Configuration:</v-card-title>

            <v-card-text class="pb-0">
               <v-form lazy-validation
                  ref="form"
                  v-model="isFormValid"
               >
                  <v-row class="pt-3 pb-2" v-if="isFilter">
                     <v-col xs="12" :md="isDD ? 4 : 12" class="pt-1 pb-0">
                        <v-select dense hide-selected persistent-hint required
                           class="pt-0 mt-0 pb-2"
                           ref="filterType"
                           append-icon="insert_chart_outlined"
                           :disabled="!chartConfig.isTypeEditable"
                           hint="* Filter Type"
                           :items="filterTypes"
                           placeholder="select a filter..."
                           :rules="[rules.required]"
                           v-model="chartConfig.filterType"
                           @change="filterTypeChanged"
                        ></v-select>
                     </v-col>
                     <!-- V260105: the following v-col -->
                     <v-col xs="12" md="4" class="pt-2 pb-0" v-if="isDD">
                        <v-text-field clearable dense persistent-hint
                           class="pt-0 pb-2"
                           ref="chartTitle"
                           autocomplete="off"
                           append-icon="title"
                           hint="Title"
                           v-model="chartConfig.title"
                        ></v-text-field>
                     </v-col>
                     <v-col xs="12" md="4" class="pt-0" v-if="isDD">
                        <v-switch
                           hide-details
                           class="py-0"
                           :disabled="chartConfig.autoSelectFirstItem"
                           :label="`Multiple Choice: ${chartConfig.multiSelect?'Yes':'No'}`"
                           v-model="chartConfig.multiSelect"
                        ></v-switch>
                     </v-col>
                  </v-row>
                  <v-row :class="isFilter ? '' : 'pt-5'">
                     <v-col :cols="chartConfig.tableType ? 4 : 12" class="pt-2 pb-3" v-if="!isFilter">
                        <v-select dense hide-selected persistent-hint
                           ref="tableType"
                           append-icon="insert_chart_outlined"
                           hint="Special Table Type"
                           :items="tableTypes"
                           placeholder="select a table..."
                           v-model="chartConfig.tableType"
                           @change="tableTypeChanged"
                        ></v-select>
                     </v-col>
                     <v-col xs="12" :md="isFilter ? 12 : 5" :class="`pt-2 pb-0${isFilter ? '' : ' pr-0'}`" v-if="chartConfig.tableType">
                        <v-text-field clearable dense persistent-hint
                           class="pt-0 pb-2"
                           ref="chartTitle"
                           autocomplete="off"
                           append-icon="title"
                           hint="Title"
                           v-model="chartConfig.title"
                        ></v-text-field>
                     </v-col>
                     <v-col xs="12" md="3" class="pt-2 pb-0 pr-0" v-if="!isFilter && chartConfig.tableType">
                        <v-text-field clearable dense persistent-hint
                           class="pt-0"
                           ref="chartIcon"
                           autocomplete="off"
                           hint="Icon"
                           v-model="chartConfig.icon"
                           :append-icon="chartConfig.icon"
                        ></v-text-field>
                     </v-col>
                  </v-row>
                  <v-row v-if="isDP">
                     <v-col cols="12" md="6" class="pt-0">
                           <!--TODO: :disabled="filterIndex === 0" -->
                        <v-select hide-selected persistent-hint
                           ref="dateColumn"
                           hint="* Date Column"
                           :disabled="filterIndex === 0"
                           :items="datePickerColumns"
                           :rules="[rules.required]"
                           v-model="chartConfig.dateColumn"
                        ></v-select>
                     </v-col>
                     <v-col cols="12" md="6" class="pt-0">
                        <v-select hide-selected persistent-hint
                           ref="preselectedDate"
                           hint="* Preselected Date"
                           :items="datePickerItems"
                           :rules="[rules.required]"
                           v-model="chartConfig.defaultId"
                        ></v-select>
                     </v-col>
                  </v-row>
                  <div v-if="isDD || (!isFilter && chartConfig.tableType)">
                     <v-row>
                        <v-col cols="12" class="pb-0">
                           <bt-model-builder
                              :debug="debug"
                              :is-dropdown="isDD"
                              :is-calculated-table="chartConfig.tableType === 'CalculatedTable'"
                              :is-paginated-table="chartConfig.tableType === 'PaginatedTable'"
                              :lake="lake"
                              :pivot-dim-in-items="pivotDimInItems"
                              :schemas="schemas"
                              :rules="chartModelRules"
                              v-model="chartModel"
                              @change="modelChanged"
                              @pivot-dim-change="getPivotDimInItems"
                           />
                        </v-col>
                     </v-row>

                     <!-- <v-row v-if="slicedData && isAdminUser" class="pt-0 pb-3"> -->
                     <v-row v-if="showSlicedData" class="pt-0 pb-3">
                        <v-col cols="12" class="pt-0 pb-0">
                           <v-textarea disabled hide-details outlined
                              label="Sliced Data"
                              :rows="slicedDataRowSize"
                              v-model="slicedData"
                           ></v-textarea>
                        </v-col>
                     </v-row>

                     <div v-if="chartConfig.tableType === 'PaginatedTable' && chartModel" class="pt-0">
                        <v-row>
                           <v-col cols="12" class="pt-0 pb-0">
                              <v-autocomplete chips deletable-chips multiple persistent-hint small-chips required
                                 ref="ptSelectedCols"
                                 autocomplete="off"
                                 hint="* Table Headers from the last Import"
                                 placeholder="choose the headers you want visible in the table..."
                                 :counter="importHeaders.length"
                                 :items="importHeaders"
                                 :loading="loadingImportHeaders"
                                 :rules="[rules.requiredArray]"
                                 v-model="chartConfig.ptSelectedCols"
                              ></v-autocomplete>
                           </v-col>
                        </v-row>
                        <v-row>
                           <v-col cols="12">
                                 <!-- @keyup="ptOtherColsChanged" -->
                              <v-text-field autofocus dense persistent-hint 
                                 ref="ptOtherCols"
                                 hint="Additional Table Headers"
                                 placeholder="add comma-separated columns not listed in the dropdown above"
                                 :rules="[rules.ptOtherCols]"
                                 v-model="chartConfig.ptOtherCols"
                              ></v-text-field>
                           </v-col>
                        </v-row>
                        <!-- <v-row>
                           <v-col cols="12">
                              <v-textarea dense outlined persistent-hint
                                 ref="ptSettings"
                                 class="pt-0"
                                 label="Settings"
                                 placeholder='{ "events":["30501"], "kpis":[{"icon":"icon1", "title":"title1", "groupBy":"30569", "operator":"avg|max|min|sum", "event_data":"timeSpent"}] }'
                                 rows="2"
                                 :rules="[rules.validPtSettings]"
                                 v-model="ptSettings"
                              ></v-textarea>
                           </v-col>
                        </v-row> -->
                     </div>

                     <!-- V260305 -->
                     <!-- <div v-else-if="chartConfig.tableType === 'CalculatedTable' || chartConfig.chartType === 'KPI'">
                        <v-row class="pt-0">
                           <v-col cols="12" class="pt-0 pb-0">
                              <bt-calculated-columns
                                 :debug="debug"
                                 :error-message="ccErrMsg"
                                 :is-required="chartConfig.tableType === 'CalculatedTable'"
                                 v-model="chartCalculatedColumns"
                                 @edit-click="calculatedColumnsClicked"
                                 @change="calculatedColumnsChanged"
                              />
                           </v-col>
                        </v-row>
                     </div> -->
                     <div v-else-if="!isDD">
                        <v-row v-if="chartTypes.length && chartConfig.tableType != 'CalculatedTable'">
                           <v-col cols="12" class="pt-3 pb-2">
                              <v-select dense hide-selected persistent-hint required
                                 class="pt-0 mt-0 pb-2"
                                 ref="chartType"
                                 append-icon="insert_chart_outlined"
                                 :items="chartTypes"
                                 :hint="`${chartConfig.chartType?'* Chart Type':''}`"
                                 placeholder="select a chart type..."
                                 :rules="[rules.required]"
                                 v-model="chartConfig.chartType"
                                 @change="chartTypeChanged"
                              ></v-select>
                           </v-col>
                        </v-row>
                                                
                        <v-row class="pt-0" v-if="showCalculated">
                           <v-col cols="12" class="pt-0 pb-3">
                              <bt-calculated-columns
                                 :clearable="isPieChart"
                                 :debug="debug"
                                 :error-message="ccErrMsg"
                                 :is-chart="chartConfig.chartType?.endsWith('Chart')"
                                 :is-required="chartConfig.tableType === 'CalculatedTable'"
                                 :max="chartConfig.chartType === 'PieChart' ? 1 : undefined"
                                 v-model="chartCalculatedColumns"
                                 @edit-click="calculatedColumnsClicked"
                                 @change="calculatedColumnsChanged"
                              />
                           </v-col>
                        </v-row>

                        <!-- V260402 -->
                        <v-row class="pt-0" v-if="chartConfig.tableType === 'CalculatedTable'">
                           <v-col xs="12" md="6" class="pt-0 pb-3">
                              <!-- V260501: Added optional chaining (?.) for model and metrics -->
                              <v-autocomplete clearable dense persistent-hint
                                 hint="Column to Sort on"
                                 placeholder="select a column..."
                                 :items="chartConfig.model?.metrics?.map(m => m.label) || []"
                                 v-model="chartConfig.sortColumn"
                              />
                           </v-col>
                           <v-col xs="12" md="6" class="pt-0 pb-3">
                              <v-select dense persistent-hint hide-selected required
                                 ref="sortOrder"
                                 class="pt-0"
                                 hint="Sort Order"
                                 :disabled="!chartConfig.sortColumn"
                                 :items="sortOrderItems"
                                 :rules="[rules.requiredSortOrder]"
                                 v-model="chartConfig.sortOrder"
                              ></v-select>
                           </v-col>
                        </v-row>

                        <!-- V260305: new -->
                        <!-- chartConfig.chartType === 'KPI' &&  -->
                        <v-row v-if="(chartCalculatedColumns || isPieChart) && chartConfig.tableType != 'CalculatedTable'"><!-- && chartCalculatedColumns != '[]'-->
                           <v-col xs="12" :md="chartConfig.metricsVisibility === 'C' ? 3: 12">
                              <!-- <v-select persistent-hint
                                 hint="Metrics Visibility"
                                 :items="getVisibilityItems()"
                                 v-model="chartConfig.metricsVisibility"   
                              ></v-select> -->
                              <v-select persistent-hint
                                 :hint="metricsVisibilityHint"
                                 :items="visibilityItems"
                                 :disabled="visibilityItems <= 1"
                                 v-model="chartConfig.metricsVisibility"   
                              ></v-select>
                           </v-col>
                           <v-col cols="9" v-if="chartConfig.metricsVisibility === 'C'">
                              <v-select persistent-hint  
                                 :chips="!isPieChart"
                                 :deletable-chips="!isPieChart"
                                 hint="Visible Metrics"
                                 placeholder="select visible metrics..."
                                 :items="visibleMetricItems"
                                 :multiple="!isPieChart"
                                 :small-chips="!isPieChart"
                                 :rules="[rules.requiredArray]"
                                 v-model="chartConfig.visibleMetrics"
                              ></v-select>
                           </v-col>
                        </v-row>
                        
                        <!-- V260511 -->
                        <v-row v-if="idResolutionItems?.length">
                           <v-col cols="12" class="py-0">
                              <bt-id-resolution-definer
                                 :available-items="idResolutionItems"
                                 :debug="debug"
                                 v-model="chartIdResolutions"
                                 @change="idResolutionsChanged"
                              />
                           </v-col>
                        </v-row>

                        <div v-if="chartConfig.chartType?.endsWith('Chart')">
                           <v-row>
                              <v-col cols="12">
                                 <v-select dense hide-selected persistent-hint required
                                    class="pt-0"
                                    hint="Colors"
                                    :items="chartColorsItems"
                                    :rules="[rules.required]"
                                    v-model="chartConfig.chartColors"
                                 ></v-select>
                              </v-col>
                           </v-row>
                           <v-row>
                              <v-col cols="12" class="py-0">
                                 <bt-chart-options
                                    class="pt-2 pb-0"
                                    :chart-type="chartConfig.chartType"
                                    :debug="debug"
                                    :dense="false"
                                    label="Chart Options in JSON Format"
                                    :rows="2"
                                    :rules="[rules.validJson]"
                                    v-model="chartOptions"
                                 />
                              </v-col>
                           </v-row>
                           <v-row v-if="needLabels">
                              <v-col cols="12" class="py-0">
                                 <v-textarea clearable outlined persistent-hint
                                    ref="chartLabels"
                                    rows="2"
                                    label="Event Labels in the JSON Array Format"
                                    hint="Set specific labels that differ from your global settings."
                                    :placeholder="labelsPlaceholder"
                                    :rules="[rules.validJsonArray]"
                                    v-model="chartConfig.chartLabels"
                                 ></v-textarea>
                              </v-col>
                           </v-row>
                        </div>
                     </div>

                     <!-- V260512: for dropdowns -->
                     <v-row v-else-if="isDD && idResolutionItems?.length">
                        <v-col cols="12" class="py-0">
                           <bt-id-resolution-definer
                              :available-items="idResolutionItems"
                              :debug="debug"
                              v-model="chartIdResolutions"
                              @change="idResolutionsChanged"
                           />
                        </v-col>
                     </v-row>
                  </div>
               </v-form>

               <!-- V260402 -->
               <v-alert v-if="errMsg" dense outlined
                  class="mt-5"
                  type="warning"
               >{{errMsg}}
               </v-alert>
            </v-card-text>

            <v-card-actions class="pt-0 pb-2 pr-3">
               <v-btn v-if="slicedData && isAdminUser"
                  text
                  color="blue darken-1"
                  @click="showSlicedData = !showSlicedData"
               >
                  {{showSlicedData ? 'Hide' : 'Show'}} Sliced Data
               </v-btn>
               <!--V260402: <div class="error--text pl-4">{{errMsg}}</div> -->
               <div class="flex-grow-1"></div>
               <v-btn text
                  class="px-0"
                  color="blue darken-1"
                  @click="cancelChartConfig"
               >Cancel</v-btn>
               <v-btn text
                  class="px-0 ml-0"
                  color="blue darken-1"
                  :disabled="!isFormChanged || !isFormValid"
                  @click="saveChartConfig"
               >Save</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <v-dialog no-click-animation persistent scrollable
         max-width="1280px"
         v-model="dialogEvents"
      >
         <v-card flat tile>
            <v-card-title class="title grey--text darken-4 font-weight-bold pb-4">Events:</v-card-title>
            <v-card-text class="px-4 py-2">
               <v-data-table dense show-expand single-expand
                  class="elevation-1 mb-2"
                  :expanded.sync="expanded"
                  :footer-props="{
                     itemsPerPageOptions:[5, 10, 20],
                     showFirstLastPage: true
                  }"
                  :headers="eventHeaders"
                  :hide-default-footer="events.length < 11"
                  item-key="__btChartExtras.id"
                  :items="events"
                  :items-per-page="10"
                  :loading="loadingEvents"
                  :loading-text="$t('loading-text')"
                  :no-data-text="$t('no-data-text', { value: 'events' })"
                  :no-results-text="$t('no-results-text', { value: 'events' })"
                  @update:expanded="eventsExpanded"
               >
                  <template v-slot:expanded-item="{ item }">
                     <!-- <td dense class="py-2" valign="top">&nbsp;</td> -->
                     <td dense
                        class="py-2" valign="top"
                        :colspan="eventHeaders.length"
                     >
                        <ul v-if="item.__btChartExtras.details && item.__btChartExtras.details.length">
                           <li v-for="(detail, j) in item.__btChartExtras.details" :key="j">
                              <span class="expanded-header">{{detail.key}}: </span>
                              <span class="expanded-content">{{detail.value}}</span>
                           </li>
                        </ul>
                        <ul v-else>
                           <li>
                              <span class="expanded-header"></span>
                              <span class="expanded-content">No details are available</span>
                           </li>
                        </ul>
                     </td>
                  </template>
               </v-data-table>
            </v-card-text>
            <v-card-actions class="py-0 pr-3">
               <div class="flex-grow-1"></div>
               <v-btn text
                  class="px-0"
                  color="blue darken-1"
                  @click="closeDialogEvents"
               >Close</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <!--<v-dialog no-click-animation persistent scrollable
         max-width="480px"
         v-model="dialogExport"
      >
         <v-card :loading="loadingDialogExport">
            <v-card-title class="title grey--text darken-4 font-weight-bold py-2">Export Configuration:</v-card-title>

            <v-card-text>
               <v-form lazy-validation
                  ref="form"
                  v-model="isFormExportValid"
               >
                  <v-row>
                     <v-col cols="12">
                        <v-text-field autofocus dense persistent-hint required
                           ref="exportName"
                           append-icon="title"
                           autocomplete="off"
                           hint="* Export Name"
                           :counter="100"
                           :maxlength="100"
                           placeholder="specify a name for your export"
                           :rules="[rules.required, rules.minLength]"
                           v-model="exportName"
                        ></v-text-field>
                     </v-col>
                  </v-row>
               </v-form>
            </v-card-text>

            <v-card-actions class="pt-0 pb-0 pr-3">
               <div class="error--text pl-4">{{errMsg}}</div>
               <div class="flex-grow-1"></div>
               <v-btn text
                  class="px-0"
                  color="blue darken-1"
                  @click="cancelExport"
               >Cancel</v-btn>
               <v-btn text
                  class="px-0 ml-0"
                  color="blue darken-1"
                  :disabled="!isFormExportValid"
                  @click="saveExport"
               >Save</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>-->

      <!-- <bt-json-viewer
         :depth="5"
         :data="item.details || {}"
         :debug="debug"
      ></bt-json-viewer> -->
   </div>
</template>

<script>
import BtAutocomplete from './BtAutocomplete.vue';
import BtChartOptions from './BtChartOptions.vue';
import BtDatePicker from "../components/BtDatePicker.vue";
import BtModelBuilder from './BtModelBuilder.vue';
import BtContactProfileForCs from "./BtContactProfileForCs.vue";
import BtCalculatedTable from "./BtCalculatedTableForLS.vue";
import BtCalculatedColumns from "./BtCalculatedColumns.vue";
import BtIdResolutionDefiner from "./BtIdResolutionDefiner.vue";  //V260511
import BtKpi from "./BtKpiForLS.vue";
// import BtJsonViewer from './BtJsonViewer.vue';
import { deepClone, findVariables, formatNumber, getAllBehavioralFields, getEventDataFields, hasOwn, isEmpty, md5Hash, sortedStringify, splitOnLast } from '../mixins/bt-mixin.js';
import { GChart } from 'vue-google-charts';
// import { sleep } from '../mixins/bt-mixin.js';
import { mapGetters } from 'vuex';  //V260511

const NAME = "BtChartForLs";
const MODELS = {
   metrics: 'metrics',
   dims: 'dims-metrics',
   dimsDim: 'dims-dim',
   date: 'date-metrics',
   dateDate: 'date-date',
   dateDim: 'date-dim',
   histogram: 'histogram-metrics',
   column: 'column-distinct'
};
const GEO_HIERARCHY = [
   ["country", "countries"],
   ["state", "states", "province", "provinces"],
   ["city", "cities", "town", "towns"]
];
const CHART_COLORS = [
   { text: "Global Dashboard Colors", value: "global"},
   { text: "Watery Blue-Greens", value: "#003b46,#07575b,#66a5ad,#c4dfe6"},
   { text: "Exotic & High-Impact", value: "#0f1f38,#8e7970,#f55449,#1b4b5a"},
   { text: "Fresh Greens", value: "#265c00,#68a225,#b3de81,#b3de99"},
   { text: "Crisp & Dramatic", value: "#505160,#68829e,#aebd38,#598234"},
   { text: "Day & Night", value: "#011a27,#063852,#f0810f,#e6df44"},
   { text: "Spicy Neutrals", value: "#af4425,#662e1c,#ebdcb2,#c9a66b"},
   { text: "Golden Afternoon", value: "#882426,#cdbea7,#323030,#c29545"}
];
const WEEKLY_CHART_FILTER = {
   standard: [
      {"$match":{"_id":"##idHolder##"}},
      {"$unwind":"$events"},
      {
         "$group":{
            "_id":{
               "dayOfWeek":{"$dayOfWeek":"$events.event_date"},
               "Event Code":"$events.event_code"
            },
            "Count":{"$sum":1}
         }
      },
      {"$sort":{"_id":1}}
   ],
   columns: []
}
const MONTHLY_CHART_FILTER = {
   standard: [
      {"$match":{"_id":"##idHolder##"}},
      {"$unwind":"$events"},
      {
         "$group":{
            "_id":{
               "year":{"$year":"$events.event_date"},
               "month":{"$month":"$events.event_date"},
               "Event Code":"$events.event_code"
            },
            "Count":{"$sum":1}
         }
      },
      {"$sort":{"_id":1}}
   ],
   columns: []
}
const KPI_FILTER = {
   standard: [
      {
         "$match": {
            "_id": "##idHolder##",
            "events": {
               "$elemMatch": {
                  "event_code": {"$in": ["##eventCodeHolder##"]}
               }
            }
         }
      },
      {
         "$project": {
            "events": {
               "$filter": {
                  "input": "$events",
                  "as": "event",
                  "cond": {
                     "$and": [
                        {"$in": ["$$event.event_code",["##eventCodeHolder##"]]}
                     ]
                  }
               }
            }
         }
      },
      {"$unwind": "$events"},
      {
         "$group": {
            "_id": {"Event Code": "$events.event_code"}
         }
      }
   ],
   columns: []
}
const NOT_EXISTED = 'Not Existed';
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['', 'Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
// V260511
const PREDEFINED_ID_RESOLUTIONS = [
   { dim: 'importId',  id: '_id', name: 'name', endpoint: '/imports',  systemDim: true },
   { dim: 'exportId',  id: '_id', name: 'name', endpoint: '/exports',  systemDim: true },
   { dim: 'actionId',  id: '_id', name: 'name', endpoint: '/actions',  systemDim: true },
   { dim: 'triggerId', id: '_id', name: 'name', endpoint: '/triggers', systemDim: true }
];

class Chart {
   constructor(chart, isFilter) {
      this.model = chart.model || {};
      this.title = chart.title || '';
      this.options = isEmpty(chart.options) ? {} : {...chart.options};
      if (isFilter) {
         this.filterType = chart.filterType || '';
         this.dateColumn = chart.dateColumn || '';
         this.defaultId = chart.defaultId || '';
         this.maxRange = 365;
         this.multiSelect = chart.multiSelect || false;
         this.autoSelectFirstItem = chart.autoSelectFirstItem || false;
         this.lastValues = chart.lastValues || [];
         this.isTypeEditable = chart.isTypeEditable;
      } else {
         this.chartType = chart.chartType || '';
         if (this.chartType === 'PaginatedTable' || this.chartType === 'CalculatedTable')
            this.tableType = this.chartType;
         else if (this.chartType)
            this.tableType = 'other';
         else
            this.tableType = '';

         this.icon = chart.icon || '';
         this.chartColors = chart.chartColors || 'global';
         this.chartLabels = chart.chartLabels || '';
         // for PaginatedTable
         this.ptSettings = chart.ptSettings || {};
         this.ptColumns = chart.ptColumns || [];
         this.ptSelectedCols = [];
         this.ptOtherCols = '';
         // for CalculatedTable & KPI
         // this.ctCalculatedColumns = chart.ctCalculatedColumns || [];
         // this.ctColumnsSequence = chart.ctColumnsSequence || '';
         this.calculatedColumns = chart.ctCalculatedColumns || chart.kpiCalculatedColumns || chart.calculatedColumns || [];
         this.columnsSequence = chart.ctColumnsSequence || chart.kpiColumnsSequence || chart.columnsSequence || '';
         this.sortColumn = chart.ctSortColumn || chart.kpiSortColumn || chart.sortColumn || '';
         this.sortOrder = chart.ctSortOrder || chart.kpiSortOrder || chart.sortOrder || '';
         // For KPI
         if (chart.metricsVisibility)
            this.metricsVisibility = chart.metricsVisibility;
         else if (chart.kpiVisibility)
            this.metricsVisibility = chart.kpiVisibility;
         else
            this.metricsVisibility = 'A';
         
         if (this.metricsVisibility === 'C')
            this.visibleMetrics = chart.visibleMetrics || chart.kpiVisibleMetrics;
         else
            this.visibleMetrics = [];
      }

      // V260511
      this.idResolutions = chart.idResolutions || [];
   }
}

function _jsonParse (strVal, retVal = {}) {
   if (strVal) return JSON.parse(strVal);
   else return retVal;
   // else return {};
}

function _validateJson(value) {
   try {
      if (value) {
         if (value.trim().indexOf('"') === 0)
            return 'SyntaxError: Unexpected token in JSON at position 0';
         JSON.parse(value);
      }
      return true;
   } catch (error) {
      return error.toString();
   }
}

function _validateJsonArray(value) {
   try {
      let json;
      if (value) {
         json = JSON.parse(value);
         return Array.isArray(json) || 'Should be an array';
      } else return true;
   } catch (error) {
      return error.toString();
   }
}

// function _isValidGeoHierarchy(geoFields) {
//    const normalizedGeoFields = geoFields.map(f => f.toLowerCase());

//   // Map each geo field to its hierarchy level
//    const levels = normalizedGeoFields.map(field => {
//       for (let i = 0; i < GEO_HIERARCHY.length; i++) {
//          if (GEO_HIERARCHY[i].some(h => h === field)) {
//             return i;
//          }
//       }

//       return -1; // invalid field
//    });

//    // Check if all fields are recognized
//    if (levels.includes(-1)) return false;

//    // Check if levels are in ascending order (i.e., proper hierarchy)
//    for (let i = 1; i < levels.length; i++) {
//       if (levels[i] < levels[i - 1]) return false;
//    }

//    return true;
// }

function _isValidGeoHierarchy(geoFields) {
   const allowed = GEO_HIERARCHY.flat();

   return geoFields.every(item =>
      allowed.includes(item.toLowerCase())
   );
}

function _validatePtSettings(strVal) {
   // {
   //    "events": ["30501", "60521", "20103"],
   //    "kpis": [
   //       { "icon":"icon1", "title": "title1", "groupBy": "30569", "operator": "avg|max|min|sum", "event_data": "timeSpent"},
   //       { "icon":"icon2", "title": "title2", "groupBy": "30503", "operator": "count"}
   //    ] 
   // }
   let result = _validateJson(strVal);
   let jsonVal;
   if (typeof result === 'boolean' && strVal) {
      jsonVal = JSON.parse(strVal);
      const keys = Object.keys(jsonVal);
      for (var i=0; i < keys.length; i++) {
         if (keys[i] != 'events' && keys[i] != 'kpis')
            return 'Unknown key: ' + keys[i];
      }

      if (jsonVal.events && !Array.isArray(jsonVal.events))
         return 'events should be an array!';

      if (jsonVal.kpis)
         result = _validateJsonArray(JSON.stringify(jsonVal.kpis));

      if (typeof result === 'boolean') {
         for (i=0; i < jsonVal.kpis.length; i++) {
            const kpi = jsonVal.kpis[i];
            if (!kpi.groupBy)
               return 'GroupBy is required!';
            if (['avg','max','min','sum'].includes(kpi.operator)) {
               if (!kpi.event_data)
                  return 'event_data is required!';
            } else if (kpi.operator != 'count')
               return 'Invalid operator: ' + kpi.operator;
         }
      }
   }

   return result;
}

function _getListItems(strVal) {
   const items = [];
   if (strVal.trim())
      strVal.trim().split(',').forEach(v => {
         items.push(v.trim().toLowerCase());
      });
   return items;
}

// V260520
function _to2Decimals(val) {
   return parseFloat(val).toFixed(2);
}

export default {
   name: NAME,

   components: {
      GChart,
      BtAutocomplete,
      BtChartOptions,
      BtDatePicker,
      BtModelBuilder,
      BtContactProfileForCs,
      BtCalculatedTable,
      BtCalculatedColumns,
      BtIdResolutionDefiner,  //V260511
      BtKpi
      // BtJsonViewer
   },

   props: {
      value: {
         type: Object,
         required: true,
         default: () => new Chart({})
      },
      // V260226: new
      aid: {
         type: Number,
         required: true
      },
      btHelpers: {
         type: Object
      },
      btnTooltip: {
         type: String,
         default: 'click to edit the chart features'
      },
      canToggle: {
         type: Boolean,
         default: false
      },
      /* V260320 - start */
      // chartsSettings: {
      //    type: Object
      // },
      dashboardsLabels: {
         type: Array
      },
      /* V260320 - end */
      colSize: {
         type: Number,
         required: true
      },
      csApiService: {
         type: Object,
      },
      datePickerColumns: {
         type: Array,
         default: () => []
      },
      debug: {
         type: Boolean,
         default: false
      },
      errorMessage: {
         type: String
      },
      filterIndex: {
         type: Number
      },
      globalChartColors: {
         type: String
      },
      isAdminUser: {
         type: Boolean,
         default: false
      },
      isEditing: {
         type: Boolean,
         default: false
      },
      isFilter: {
         type: Boolean,
         default: false
      },
      isReady: {
         type: Boolean,
         default: false
      },
      lake: {
         type: Object,
         required: true,
         default: () => {}
      },
      lsApiService: {
         type: Object,
         required: true
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      },
      slicedData: {
         type: String
      }
   },

   data() {
      return {
         gChartSettings: {
            packages: ['corechart', 'table', 'map', 'geochart'],
            mapsApiKey: process.env.VUE_APP_GOOGLE_API_KEY
         },
         rules: {
            required: value => !!value || "Value is required!",
            requiredArray: v => (v && v.length > 0) || 'Value is required!',
            requiredSortOrder: value => {
               if (this.chartConfig.sortColumn)
                  return !!value || 'Value is required!';
               else return true;
            },
            validJson: value => {
               return _validateJson(value);
            },
            minLength: value => value.length >= 5 || "Name should have more than 5 characters!",
            validPtSettings: value => {
               return _validatePtSettings(value);
            },
            ptOtherCols: value => {
               let result = '';
               const items = _getListItems(value);
               if (items.length) {
                  const processedItems = [];
                  items.forEach(v => {
                     if (!v)
                        result += 'Header cannot be empty! ';
                     else if (v.includes(' '))
                        result += `'${v}' cannot have spaces! `;
                     else if (this.importHeaders.find(h => h.value === v))
                        result += `'${v}' exists in the Import headers! `;
                     else if (processedItems.find(f => f === v))
                        result += `'${v}' is duplicate! `;
                     else processedItems.push(v);
                  });
               }
               return result === '' || result;
            },
            validJsonArray: value => {
               try {
                  let json;
                  if (value) {
                     json = JSON.parse(value);
                     if (Array.isArray(json)) {
                        if (json.length) {
                           const invalids = [];
                           json.forEach((element, i) => {
                              const keys = Object.keys(element);
                              if (keys.length != 2 || keys[0] != 'id' || keys[1] != 'label')
                                 invalids.push(i + 1);
                           });
                           return invalids.length === 0 || `Items must only have "id" and "label" properties: ${invalids.join(', ')}!`;
                        } else 
                           return 'The array should have one or more items!';
                     } else
                        return 'Should be an array!';
                  } else {
                     return true;
                  }
               } catch (error) {
                  return error.toString();
               }
            }
         },
         filterTypes: [],
         tableTypes: [
            { text: 'Table with Pagination', value: 'PaginatedTable' },
            { text: 'Table with Calculated Columns', value: 'CalculatedTable' },
            { text: 'Chart/KPI/Table', value: 'other' }
         ],
         chartTypes: [],
         chartColorsItems: [
            { text: "Global Dashboard Colors", value: "global"},
            { text: "Watery Blue-Greens", value: "#003b46,#07575b,#66a5ad,#c4dfe6"},
            { text: "Exotic & High-Impact", value: "#0f1f38,#8e7970,#f55449,#1b4b5a"},
            { text: "Fresh Greens", value: "#265c00,#68a225,#b3de81,#b3de99"},
            { text: "Crisp & Dramatic", value: "#505160,#68829e,#aebd38,#598234"},
            { text: "Day & Night", value: "#011a27,#063852,#f0810f,#e6df44"},
            { text: "Spicy Neutrals", value: "#af4425,#662e1c,#ebdcb2,#c9a66b"},
            { text: "Golden Afternoon", value: "#882426,#cdbea7,#323030,#c29545"}
         ],
         isFirstLoad: true,
         loadingChartData: false,
         loadingTableData: false,
         loadingImportHeaders: false,
         dialogChartConfig: false,
         isFormValid: false,
         chart: {},      //the copy of value
         chartConfig: {},  //the copy of chart
         chartData: [[], []],    //the copy of data
         chartModel: '',
         chartModelRules: [],
         chartOptions: '',
         lastChartType: '',
         isModelChanged: true,
         errMsg: '',
         tableHeaders: [],
         // isChart: false,
         isCalculatedTable: false,
         datePickerItems: [   //V260520: Added Today and Yesterday
            { text: "Today", value: "T" },
            { text: "Current Month", value: "TM" },
            { text: "Current Quarter", value: "TQ" },
            { text: "Current Year", value: "TY" },
            { text: "Yesterday", value: "Y" },
            { text: "Last Month", value: "LM" },
            { text: "Last Quarter", value: "LQ" },
            { text: "Last Year", value: "LY" }
         ],
         datePickerRange: [],
         chartEvents: {
            select: () => {
               if (this.isEditing || (this.isPieChart && this.chart.calculatedColumns?.length)) {
                  this.$refs.gChart.chartObject.setSelection([]);
                  return;
               }

               const selection = this.$refs.gChart.chartObject.getSelection();
               // alert('in chartEvents.select: selection: ' + JSON.stringify(selection));
               if (!selection.length) {
                  this.$emit('chart-reclick');
                  return;
               }

               const row = selection[0].row + 1;
               const col = selection[0].column;

               // We ignore clicks on the calculated columns.
               if (this.chart.calculatedColumns?.find(cc => cc.name === this.chartData[0][col])) {
                  this.$refs.gChart.chartObject.setSelection([]);
                  return;
               }

               const query = this.chart.model;
               let dimKeys, pivotDim, dateField, dateParts2, histogramColumn;
               let resolveLabel = false;
               switch (query.type) {
                  case MODELS.dims:
                     //V260128.2: dimKeys = this.getDims(query.dims);
                     dimKeys = query.dims.map(d => {
                        //V260128.3: const parts = d.split(' as ');
                        const parts = splitOnLast(d, ' as ');
                        return parts[0];
                     });
                     resolveLabel = true;
                     break;
                  case MODELS.dimsDim:
                     dimKeys = query.groupByDims;
                     pivotDim = query.pivotDim;
                     break;
                  case MODELS.date:
                  case MODELS.dateDim:
                     dimKeys = query.dateParts;
                     dateField = query.dateField;
                     pivotDim = query.pivotDim;
                     break;
                  case MODELS.dateDate:
                     dimKeys = [query.dateParts[0]];
                     dateField = query.dateField;
                     dateParts2 = query.dateParts[1];
                     break;
                  case MODELS.histogram:
                     histogramColumn = query.histogramOn;
                     break;
                  default:
                     alert(`Error in select(): Unsupported model: ${query.type}`);
                     break;
               }


               // alert(JSON.stringify(this.chartData[0][col]) + ', col='+col)

               // // const selectColumns = [];
               // // const selectData = [];
               // // if (histogramColumn) {
               // //    selectColumns.push(histogramColumn);
               // //    const val = values[0].trim();
               // //    if (val.startsWith('<')) {
               // //       selectData.push(`${histogramColumn} < ${Number(val.replace('<', '').trim())}`);
               // //    } else if (val.startsWith('>')) {
               // //       selectData.push(`${histogramColumn} > ${Number(val.replace('>', '').trim())}`);
               // //    } else if (val.includes('-')) {
               // //       const [min, max] = val.split('-').map(v => Number(v.trim()));
               // //       selectData.push(`${histogramColumn} between ${min} and ${max}`);
               // //    }
               // // } else {
               // //    for (let i = 0; i < dimKeys.length; i++) {
               // //       const key = dimKeys[i];
               // //       if (dateField) {
               // //          selectColumns.push(dateField);
               // //          selectData.push(`${key}(${dateField}) = ${Number(values[i])}`);
               // //       } else {
               // //          selectColumns.push(key);
               // //          selectData.push(`${key} = '${values[i]}'`);
               // //       }
               // //    }
               // // }

               // const values = this.chartData[row][0].split(':');
               const {selectColumns, selectData} = this.getSelectColumnsAndData(
                  // values,
                  this.chartData[row][0].v.split(':'),   //V2605112: added .v
                  dimKeys,
                  dateField,
                  histogramColumn,
                  resolveLabel
               );

               if (pivotDim) {
                  selectColumns.push(pivotDim);
                  /* V260108: start */
                  let label = this.chartData[0][col];
                  /* V260320: start */
                  if (this.effectiveLabels) {
                     // const labels = JSON.parse(this.chart.chartLabels);
                     if (pivotDim === 'event_code') {
                        const lbl = this.effectiveLabels.find(l => l.label === label);
                        if (lbl) label = lbl.id;
                     } else {
                        const eventCodeInd = dimKeys.findIndex(dim => dim === 'event_code');
                        if (eventCodeInd > -1) {
                           const match = selectData[eventCodeInd].match(/'([^']*)'/);
                           if (match) {
                              const lbl = this.effectiveLabels.find(l => l.label === match[1]);
                              if (lbl) {
                                 selectData[eventCodeInd] = selectData[eventCodeInd].replace(match[1], lbl.id);//`'${lbl.id}'`
                              } 
                           }
                        }
                     }
                  }
                  /* V260320: end */
                  /* V260108: end */
                  selectData.push(`${pivotDim} = '${label}'`); //this.chartData[0][col]
               } else if (dateParts2) {
                  selectData.push(`${dateParts2}(${dateField}) = ${this.chartData[0][col].replace(dateParts2 , '').trim()}`);
               }

               const chartClickData = {
                  columns: selectColumns,
                  data: selectData
               }

               // V260313
               const clickedMetric = query.metrics.find(metric => metric.label === this.chartData[0][col || 1]);
               if (clickedMetric) {
                  chartClickData.filter = clickedMetric.filter;
                  chartClickData.exclude = clickedMetric.exclude;
               }

               this.$emit('chart-click', chartClickData);
            },
            // V260127
            ready: () => {
               this.loadingChartData = false;
            }
         },
         dropdownSwitch: true,
         dropdownSelections: [],
         chartCalculatedColumns: '',
         autoSelectFirstItemDisabled: false,
         eventDataFields: getEventDataFields(),
         eventDataNames: [],

         // for the CalculatedTable
         sortOrderItems: [
            { text: "Ascending", value: "asc" },
            { text: "Descending", value: "desc" }
         ],
         modelDims: [],
         modelLabels: [],
         exportCalculatedTable: false,
         ccErrMsg: '',

         // for export
         dialogExport: false,
         isFormExportValid: false,
         loadingDialogExport: false,
         exportName: '',
         lastExport: null,
         importValidations: null,
         exportFields: null,
         queryResult: [],
         queryResultHeaders: [],
         pivotDimInItems: [],
         strChartConfig: '',
         initChartConfig: '',
         clickedRowInd: -1,
         calCols: [],

         // for PaginatedTable
         importHeaders: [],
         ptLoading: false,
         ptSettings: '',
         ptItemsCount: 0,
         ptOptions: {},
         ptHeaders: [],
         isPtHeadersNeeded: true,
         ptColspan: [],

         // for ContactProfile
         cpKpisData: [],
         weeklyChartData: [],
         monthlyChartData: [],

         // for events
         dialogEvents: false,
         loadingEvents: false,
         eventHeaders: [],
         events: [],
         expanded: [],
         showSlicedData: false,
         labelsPlaceholder: 'example: [{"id": "70112", "label": "Email Sent"}]',
         effectiveLabels: null,
         chartIdResolutions: '', //V260511
         idResolutionsData: {}   //V260511

         //For KPI: V260305
         // kpiVisibilityItems: [
         //    { text: "Show All", value: "A" },
         //    { text: "Show None", value: "N" },
         //    { text: "Custom...", value: "C" }
         // ]
      }
   },

   computed: {
      // V260511
      ...mapGetters(['token']),

      vCardTextClass() {
         // let result = 'py-2 pl-3 ';
         // return result + (this.isEditing ? 'pr-0' : 'pr-3');
         return 'py-2 px-3';
      },

      isTable() {
         return this.chart.chartType === 'Table';
      },

      // isChart() {
      //    return !this.isFilter && !this.isTable && this.chart?.chartType && this.chart.chartType != 'KPI';
      // },

      datePickerPlaceholder() {
         let placeholder = 'Date Picker';

         if (this.datePickerItems.length > 0 && this.chartConfig.defaultId) {
            const myItem = this.datePickerItems.find(item => item.value === this.chartConfig.defaultId)
            if (myItem != undefined)
               placeholder = myItem.text;
         }

         return placeholder;
      },

      canCreateExport() {
         return this.$store.getters.user.policies.includes(`contact-export-create`);
      },

      isDP() {
         return this.chartConfig.filterType === 'DatePicker';
      },

      isDD() {
         return this.chartConfig.filterType === 'Dropdown';
      },

      isFormChanged() {
         //TODO: this.isFormValid,this.$refs.form?.validate() && 
         const isChanged = this.strChartConfig !== this.initChartConfig;
         return isChanged;
      },

      slicedDataRowSize() {
         return this.chart.model?.type.endsWith('-metrics') ? 3 : 2;
      },

      behFields() {
         return getAllBehavioralFields();
      },

      behFieldsForGroupby() {
         return this.behFields.filter(f => f.value != 'ip' && f.value != 'pdid');
      },

      // V260128: Included 'dims-* models as well.
      needLabels() {
         const { type, groupByDims, pivotDim, dims } = this.chartConfig?.model;  // || this.chart.model;
         // return type?.endsWith('-dim') &&
         //    (groupByDims?.includes('event_code') || pivotDim === 'event_code')
         return (
            (type?.endsWith('-dim') &&
            (groupByDims?.includes('event_code') || pivotDim === 'event_code')) ||
            (type?.startsWith('dims-') && dims?.includes('event_code'))
         );
      },

      // V260318
      isPieChart() {
         const ct = this.chartConfig?.chartType || this.chart?.chartType;
         return ct === 'PieChart' || ct === 'DonutChart';
      },

      // V260318
      showCalculated() {
         return (this.chartConfig.tableType === 'CalculatedTable' && !isEmpty(this.chartConfig.model))
            ||
            (this.chartConfig.chartType && this.chartConfig.model?.type?.endsWith('metrics'));
      },

      // V260318
      visibilityItems() {
         let items;
         // if (this.chartConfig.chartType === 'PieChart') {
         if (this.isPieChart) {
            if (this.chartCalculatedColumns)
               items = [{ text: "Calculated Column", value: "N" }];
            else if (this.visibleMetricItems.length === 1)
               items = [{ text: "Specified Metric", value: "A" }];
            else
               items = [{ text: "Select Metric...", value: "C" }];
         } else {
            items = [
               { text: "Show All Metrics", value: "A" },
               { text: "Show No Metrics", value: "N" }
            ];

            if (this.visibleMetricItems.length > 1)
               items.push({ text: "Select Metrics...", value: "C" });
         }

         return items;
      },

      // V260305
      // kpiMetrics() {
      //    return this.chartConfig?.model.metrics.map(m => m.label);
      // },

      // V260318
      visibleMetricItems() {
         return this.chartConfig.model?.metrics?.map(m => m.label) || [];
      },

      metricsVisibilityHint() {
         let hint = 'Metrics Visibility';
         if (this.chartCalculatedColumns && !this.isPieChart)
            hint += ': All Calculated Columns will be included';
         return hint;
      },

      // V260512: Added filterType
      // V260511: Added
      idResolutionItems() {
         const { model, filterType, tableType, chartType } = this.chartConfig;
         let resolvedItems;
         if (!isEmpty(model) && (filterType === 'Dropdown' || tableType === 'CalculatedTable' || chartType?.endsWith('Chart'))) {
            let dims;
            if (model.type === MODELS.dims)
               dims = model.dims;
            else if (model.type === MODELS.dimsDim)
               dims = model.groupByDims;
            else if (model.type === MODELS.column)
               dims = [model.column];
            else dims = [];

            resolvedItems = dims.map(dimName => {
               // 1. Look for an existing definition
               const predefined = PREDEFINED_ID_RESOLUTIONS.find(item => item.dim === dimName);

               // 2. Return the predefined one if found, otherwise the default fallback
               return predefined ? { ...predefined } : { dim: dimName, systemDim: false };
            });
         } else resolvedItems = [];

         return resolvedItems;
      }
   },

   watch: {
      value: { //Object: should it be value.model
         immediate: true,
         deep: true,
         async handler(newVal) { //, oldVal
            // console.warn(`in v-model watch: newVal=${JSON.stringify(newVal)}\noldVal=${JSON.stringify(oldVal)}\n${sortedStringify(newVal) === sortedStringify(oldVal)}`);
            // if (newVal?.filterType === 'Dropdown')
            //    alert('in dashboard.globalFilters watch');
            if (newVal) {
               await this.init();
            }
         }
      },

      datePickerRange: {   //Array
         immediate: false,
         deep: true,
         handler (arrDates) {
            if (!this.isEditing)
               this.$emit('date-change', arrDates, this.selectedDateValue);
         }
      },

      chartConfig: { //Object
         immediate: false,
         deep: true,
         handler (val) {
            this.strChartConfig = sortedStringify(val);
         }
      },

      chartOptions: {   //String
         immediate: false,
         handler (val) {
            this.chartConfig.options = _jsonParse(val);
         }
      },

      globalChartColors: { //String
         immediate: false,
         handler (val) {
            if (val && this.chart.chartType !== 'KPI' && this.chart.chartType !== 'Table' && this.chart.chartColors === 'global') {
               this.setChartColors(this.chart, val);
               this.$emit('input', this.chart)
            }
         }
      },

      ptOptions: {
         immediate: false,
         handler () {
            // alert(`in watch: isFirstLoad=${this.isFirstLoad}, sortBy.len=${val.sortBy.length}\ntableOptions=${JSON.stringify(val)}`);
            // if (val.sortBy.length) {
            //    const sort = {};
            //    sort[val.sortBy[0]] = val.sortDesc[0] ? -1 : 1;
            //    this.chart.filter.sort = sort;
            // }

            if (!this.isFirstLoad) {
               this.getPaginatedTableData();
            }
         }
      },

      ptSettings: {  //String
         immediate: false,
         handler (val) {
            // alert(`in ptSettings watch: val=${val}`);
            this.chartConfig.ptSettings = val ? _jsonParse(val) : {};
         }
      },

      // V260305
      visibilityItems: {  //Array
         immediate: false,
         handler (val) {
            // alert(`in visibilityItems watch: val=${val}`);
            if (val?.length <= 1 || !this.chartConfig.metricsVisibility)
               this.chartConfig.metricsVisibility = val[0].value;
         }
      }
   },

   methods: {
      isEmpty,
      sortedStringify,

      log(msg, _alert =false) {
         if (this.debug) {
            console.log(`-----${NAME} V260520.1 says => ${msg}`);
            if (_alert) {
               alert(`${NAME} says:\n${msg}`);
            }
         }
      },

      //TODO: pass newVal & oldVal and compare models not to call get files unnecessarily.
      async init() {
         this.chart = new Chart(this.value, this.isFilter);
         // this.log('in init/start: chart=' + sortedStringify(this.chart));
         const { filterType, chartType, model } = this.chart;
         if (filterType != 'DatePicker' && !this.isEditing && this.isReady && !isEmpty(model)) {
            this.loadingChartData = true;
            
            // V260320
            if (this.chart.chartLabels)
               this.effectiveLabels = JSON.parse(this.chart.chartLabels);
            else if (this.dashboardsLabels)
               this.effectiveLabels = this.dashboardsLabels;

            if (chartType === 'PaginatedTable') {
               this.isFirstLoad = true;
               await this.getPaginatedTableCount(model);
               if (this.ptItemsCount) {
                  this.ptOptions.page = 1;   //V251124
                  this.isPtHeadersNeeded = true;
                  await this.getImportHeaders();
                  await this.getPaginatedTableData();
                  this.isFirstLoad = false;
                  const len = this.chart.ptColumns.length + 1;
                  this.ptColspan = [];
                  this.ptColspan.push(Math.ceil(len / 3));
                  this.ptColspan.push(Math.ceil((len - this.ptColspan[0]) / 2));
                  this.ptColspan.push(len - this.ptColspan[0] - this.ptColspan[1]);
               }
            } else {
               // V260305
               this.calCols = deepClone(this.chart.calculatedColumns || [])
                  .map(cc => ({
                     ...cc,
                     vars: findVariables(cc.expression)
                  }));

               // V260511
               await this.getIdResolutionsData(this.chart.idResolutions);

               await this.getChartData(model, filterType || chartType);
            }

            if (filterType === 'Dropdown')
               this.$emit('data-change', this.chartData);

            // V260520: Added chartData.length condition
            // V260127 & V260224 && V260305.2
            if (
                  !this.chartData?.length ||
                  (!chartType?.endsWith('Chart') && chartType != 'CalculatedTable' && chartType != 'KPI')
            ) {
               this.loadingChartData = false;
            }
         }

         this.isModelChanged = false;
      },

      dropdownSwitchChanged(val) {
         this.dropdownSwitch = val;
         if (this.dropdownSelections.length)
            this.fireDropdownChange();
      },

      dropdownSelectionsChanged(arrVal) {
         // _alert('in dropdownSelectionsChanged(): arrVal=' + JSON.stringify(arrVal));
         this.dropdownSelections = [...arrVal];
         this.fireDropdownChange();
         // this.chart.lastValues = this.dropdownSelections
         // this.$emit('input', this.chart);
      },

      fireDropdownChange() {
         // alert('in fireDropdownChange(): dropdownSelections=' + JSON.stringify(this.dropdownSelections));
         const jsonVal = {
            $dropdownSwitch: this.dropdownSwitch
         };
         jsonVal[this.chart.model.column] = this.dropdownSelections.length === 1 && this.dropdownSelections[0] == null ? [] : this.dropdownSelections;
         this.$emit('dropdown-change', jsonVal);
      },

      switchBetweenChartAndTable() {
         if (this.isTable)
            this.chart.chartType = this.lastChartType;
         else {
            this.lastChartType = this.chart.chartType;
            this.chart.chartType = 'Table';
         }
      },

      async contactProfileClicked(item) {
         await this.ptItemExpanded([item]);

         const weeklyChartFilter = JSON.parse(
            JSON.stringify(WEEKLY_CHART_FILTER).replace('##idHolder##', item._id)
         );
         const weeklyData = await this.btHelpers.getData(
            null, //importId: not in use
            weeklyChartFilter,
            undefined,  //page
            undefined,  //limit
            false //distinct
         );
         this.weeklyChartData = this.prepareContactProfileChartData(weeklyData, WEEKLY_CHART_FILTER);

         const monthlyChartFilter = JSON.parse(
            JSON.stringify(MONTHLY_CHART_FILTER).replace('##idHolder##', item._id)
         );
         const monthlyData = await this.btHelpers.getData(
            null, //importId: not in use
            monthlyChartFilter,
            undefined,  //page
            undefined,  //limit
            false //distinct
         );
         const processedMonthlyData = this.processContactProfileChartData(monthlyData);
         this.monthlyChartData = this.prepareContactProfileChartData(processedMonthlyData, MONTHLY_CHART_FILTER);

         this.cpKpisData = [];
         for (var i = 0; i < this.chart.ptSettings?.kpis?.length; i++) {
            const kpi = this.chart.ptSettings.kpis[i];
            let operatorDescHolder, groupIdValHolder;

            if (kpi.operator === 'count') {
               operatorDescHolder = "Count";
               groupIdValHolder = {$sum: 1};
            } else {
               const operand = '$events.event_data.' + kpi.event_data;
               switch (kpi.operator) {
                  case 'avg':
                     operatorDescHolder = "Average";
                     groupIdValHolder = {$avg: operand};
                     break;
                  case 'max':
                     operatorDescHolder = "Maximum";
                     groupIdValHolder = {$max: operand};
                     break;
                  case 'min':
                     operatorDescHolder = "Minimum";
                     groupIdValHolder = {$min: operand};
                     break;
                  case 'sum':
                     operatorDescHolder = "Total";
                     groupIdValHolder = {$sum: operand};
                     break;
                  default:
                     operatorDescHolder = "Unknown";
                     groupIdValHolder = {};
                     groupIdValHolder['$' + kpi.operator] = operand;
                     break;
               }
            }

            let kpiFilter = JSON.parse(
               JSON.stringify(KPI_FILTER)
                  .replace ('##idHolder##', item._id)
                  .replace (/##eventCodeHolder##/g, kpi.groupBy)
            );

            const $group = kpiFilter.standard.find(f => f.$group).$group;
            $group[operatorDescHolder] = groupIdValHolder;

            const kpiData = await this.btHelpers.getData(
               null, //importId: not in use
               kpiFilter,
               undefined,  //page
               undefined,  //limit
               false //distinct
            );
            this.cpKpisData.push(kpiData.length ? kpiData[0] : {});
         }         
      },

      processContactProfileChartData(dataIn) {
         const dataOut = [];
         if (!dataIn?.length) return dataOut;
         
         const idKeys = Object.keys(dataIn[0]._id);
         const otherKey = Object.keys(dataIn[0])[1];
         if (idKeys.length === 3) {
            const yearIndex = idKeys.findIndex(k => k === 'year');
            if (yearIndex === 0) {
               dataIn.forEach(d => {
                  const id = { "year-month": d._id.year + '-' + d._id.month };
                  id[idKeys[2]] = d._id[idKeys[2]];
                  const row = { _id: id };
                  row[otherKey] = d[otherKey];
                  dataOut.push(row);
               });
            } else if (yearIndex === 1) {
               dataIn.forEach(d => {
                  const id = {};
                  id[idKeys[0]] = d._id[idKeys[0]];
                  id["year-month"] = d._id.year + '-' + d._id.month;
                  const row = { _id: id };
                  row[otherKey] = d[otherKey];
                  dataOut.push(row);
               });
            } else {
               const monthIndex = idKeys.findIndex(k => k === 'month');
               if (monthIndex === 0) {
                  dataIn.forEach(d => {
                     const id = { "month-dayOfMonth": d._id.month + '-' + d._id.dayOfMonth };
                     id[idKeys[2]] = d._id[idKeys[2]];
                     const row = { _id: id };
                     row[otherKey] = d[otherKey];
                     dataOut.push(row);
                  });
               } else if (monthIndex === 1) {
                  dataIn.forEach(d => {
                     const id = {};
                     id[idKeys[0]] = d._id[idKeys[0]];
                     id["month-dayOfMonth"] = d._id.month + '-' + d._id.dayOfMonth; 
                     const row = { _id: id };
                     row[otherKey] = d[otherKey];
                     dataOut.push(row);
                  });
               }
            }
         }

         return dataOut;
      },

      prepareContactProfileChartData(dataIn, filter) {
         let data = this.chart?.ptSettings?.events?.length
            ? dataIn.filter(d => this.chart.ptSettings.events.includes(d._id['Event Code']))
            : dataIn;

         this.inject2ndDimension(data);

         const idKeys = [];
         Object.keys(data[0]._id).forEach(key => {
            if (key != 'isRootInsert' && key != 'elm')
               idKeys.push(key);
         });

         const dataKey = Object.keys(data[0])[1];
         let newIdKey = filter.standard.find(f => f.$group).$group._id[idKeys[0]];

         if (typeof newIdKey === 'object')
            newIdKey = newIdKey['$' + idKeys[0]];

         newIdKey = newIdKey ? newIdKey.replace('$events.', '').replace('$', '') : '';

         let newIdField = this.importHeaders.find(f => f.value === newIdKey);
         if (!newIdField)
            newIdField = this.behFieldsForGroupby.find(f => f.value === newIdKey);

         let idFieldPostfix = ' / ';
         let valueNames;
         let convertToString = false;
         switch (idKeys[0]) {
            case 'year':
               idFieldPostfix += 'Yearly';
               convertToString = true;
               break;
            case 'month':
               idFieldPostfix += 'Monthly';
               valueNames = MONTHS;
               break;
            case 'dayOfMonth':
               idFieldPostfix += 'Daily';
               convertToString = true;
               break;
            case 'hour':
               idFieldPostfix += 'Hourly';
               convertToString = true;
               break;
            case 'dayOfWeek':
               idFieldPostfix += 'Day of the Week';
               valueNames = DAYS;
               break;
            default:
               idFieldPostfix = '';
               break;
         }

         const chartData = [];
         let chartDataItem;
         if (idKeys.length === 1) {
            if (idFieldPostfix)
               chartData.push([newIdField.text + idFieldPostfix].concat(dataKey));
            else
               chartData.push(idKeys.concat(dataKey));

            data.forEach(d => {
               chartDataItem = valueNames
                  ? valueNames[[d._id[idKeys[0]]]]
                  : chartDataItem = d._id[idKeys[0]];
               chartData.push([convertToString ? chartDataItem + '' : chartDataItem, d[dataKey]]);
            });
         } else if (idKeys.length === 2) {
            const uniqueItems1 = [...new Set( data.map(obj => obj._id[idKeys[0]])) ];
            const uniqueItems2 = [...new Set( data.map(obj => obj._id[idKeys[1]])) ];
            chartData.push([idFieldPostfix ? newIdField.text + idFieldPostfix : idKeys[0], ...uniqueItems2]);
            uniqueItems1.forEach(item1 => {
               const values = [];
               uniqueItems2.forEach(item2 => {
                  const match = data.find(d => d._id[idKeys[0]]===item1 && d._id[idKeys[1]]===item2);
                  values.push(match ? match[dataKey] : 0);
               });

               chartDataItem = valueNames ? valueNames[item1] : item1;
               chartData.push([convertToString ? chartDataItem + '' : chartDataItem, ...values]);
            });
         }

         //making the 1st row and the 1st column of other rows, string.
         for (let index = 0; index < chartData.length; index++) {
            if(index === 0) {
               for (let index_i = 0; index_i < chartData[index].length; index_i++) {
                  chartData[0][index_i] = chartData[0][index_i].toString();
               }
            } else {
               chartData[index][0] = chartData[index][0] === null ? '' : chartData[index][0].toString();
            }
         }

         const { labels } = this.chartsSettings;
         if (labels) {
            //TODO: Use the following if statement: if (chartData[0][0] === 'Event Code'). Then you need to bring the 2nd for up.
            let labelsChanged = false;
            for (let i = 1; i < chartData[0].length; i++) {
               const lbl = labels.find(l => l.id == chartData[0][i]);
               if (lbl) {
                  chartData[0][i] = lbl.label;
                  labelsChanged = true;
               }
            }

            if (!labelsChanged) {
               for (let i = 1; i < chartData.length; i++) {
                  const lbl = labels.find(l => l.id == chartData[i][0]);
                  if (lbl)
                     chartData[i][0] = lbl.label;
               }
            }
         }

         // this.log('in prepareContactProfileChartData(): chartData=' + JSON.stringify(this.chartData));
         return chartData;
      },

      /* the function will change data */
      inject2ndDimension(data) {
         // alert('in inject2ndDimension() start: data=' + JSON.stringify(data));
         const keyLengths = new Set();
         data.forEach(d => {
            keyLengths.add(Object.keys(d._id).length);
         });

         if (keyLengths.size === 2) {
            const ind2 = data.findIndex(d => Object.keys(d._id).length === 2);
            const keys2 = Object.keys(data[ind2]._id);
            data.filter(d => Object.keys(d._id).length === 1).forEach(d => {
               const key1 = Object.keys(d._id)[0];
               const val1 = d._id[key1];
               if (key1 === keys2[0]) {
                  d._id[keys2[1]] = NOT_EXISTED;
               } else {
                  d._id = {};
                  d._id[keys2[0]] = NOT_EXISTED;
                  d._id[key1] = val1;
               }
            });
         }
      },

      async eventsClicked(events) {
         // _alert('in eventsClicked(): events=' + JSON.stringify(events));
         this.dialogEvents = true;
         this.loadingEvents = true;
         // await sleep (5000);
         const headers = [];
         const mainHeaders = new Set();
         const subHeaders = new Set();

         events.forEach((event, i) => {
            Object.keys(event).forEach(key => {
               if (this.behFields.find(f => f.value != 'event_data' && f.value === key.toString()))
                  mainHeaders.add(key);
               else if (key.toString() != '__btChartExtras')
                  subHeaders.add(key);
            });
            event.__btChartExtras = { id: `${event.event_code}_${event.event_date}_${i}` };
         });

         // _alert('mainHeaders.size=' + mainHeaders.size + ', subHeaders.size=' + subHeaders.size);

         mainHeaders.forEach(h => {
            const behField = this.behFields.find(f => f.value === h);
            if (behField)
               headers.push({ text: behField.text, value: h, align: 'left', sortable: true });
         });

         this.events = [...events];
         this.eventHeaders = [...headers];
         this.eventDetailHeaders = Array.from(subHeaders);
         this.loadingEvents = false;
      },

      eventsExpanded(event) {
         // _alert('in eventsExpanded(): event=' + JSON.stringify(event));
         if (event.length === 0) return;

         const e = event[0];
         // e.details = [];
         e.__btChartExtras.details = [];
         this.eventDetailHeaders.forEach(h => {
            if (e[h])
               e.__btChartExtras.details.push({ key: h, value: e[h] });
         });
         // _alert('in eventsExpanded(): e=' + JSON.stringify(e));
      },

      closeDialogEvents() {
         this.expanded = [];
         this.dialogEvents = false;
      },

      exportCalculatedTableClicked() {
         this.exportCalculatedTable = true;
      },

      ctExported() {
         this.exportCalculatedTable = false;
      },

      async configureChart() {
         this.chartColorsItems = CHART_COLORS.filter(color => color.value != this.globalChartColors);
         this.chartTypes = [];
         this.dialogChartConfig = true;
         this.chartConfig = {...new Chart(this.chart, this.isFilter)};
         this.chartModel = isEmpty(this.chartConfig.model) ? '' : JSON.stringify(this.chartConfig.model);
         // if (!this.chartConfig.filterType && this.filterTypes.length === 1) {
         //    this.chartConfig.filterType = this.filterTypes[0].value;
         // }

         if (this.chartConfig.filterType) {
            this.filterTypeChanged(this.chartConfig.filterType);
         } else {
            if (this.isFilter && this.filterTypes.length === 1) {
               this.chartConfig.filterType = this.filterTypes[0].value;
            }

            if (this.chartConfig.chartType) {
               this.getChartTypes();
               if (this.chartConfig.chartType === 'PaginatedTable') {
                  this.ptSettings = this.chartConfig.ptSettings && Object.keys(this.chartConfig.ptSettings).length
                     ? sortedStringify(this.chartConfig.ptSettings) : '';            
                  await this.getImportHeaders();
                  this.chartConfig.ptSelectedCols = [];
                  const otherCols = [];
                  this.chartConfig.ptColumns.forEach(col => {
                     if (this.importHeaders.find(h => h.value === col))
                        this.chartConfig.ptSelectedCols.push(col);
                     else
                        otherCols.push(col);
                  });
                  this.chartConfig.ptOtherCols = otherCols.join(', ');
               } else { //if (['CalculatedTable', 'KPI'].includes(this.chartConfig.chartType)) {    // V260305
                  this.chartCalculatedColumns = this.chartConfig.calculatedColumns?.length
                     ? JSON.stringify(this.chartConfig.calculatedColumns)
                     : '';
                  // V260511
                  this.chartIdResolutions = this.chartConfig.idResolutions?.length
                     ? JSON.stringify(this.chartConfig.idResolutions)
                     : '';
               }
            }
         }

         const tempOptions = deepClone(this.chartConfig.options);
         delete tempOptions.colors;
         this.chartOptions = sortedStringify(tempOptions);//this.chart.options
         this.initChartConfig = sortedStringify(this.chartConfig);
         // this.log(`in end of configureChart(): chartConfig=${this.initChartConfig}`);
      },

      filterTypeChanged() {
         let fld;
         if (this.isDP) {
            fld = 'preselectedDate';
            this.chartModel = '';
            this.chartModelRules = [];
         } else {
            fld = 'chartTitle';
            this.chartModelRules = [ this.rules.required ];
         }

         this.$nextTick(() => {
            this.$refs[fld]?.focus();
         });
      },

      async tableTypeChanged(val) {
         if (val === 'PaginatedTable') {
            this.chartModel = JSON.stringify({
               type: 'column-distinct',
               column: '_id',
               lake: this.lake
            });
            this.modelChanged(this.chartModel);
            await this.getImportHeaders();
         } else {
            this.chartModel = '';
         }
      },

      async getImportHeaders() {
         this.loadingImportHeaders = true;
         this.importHeaders = await this.getImportHeadersFromParent();
         this.loadingImportHeaders = false;
      },

      getImportHeadersFromParent() {
         return new Promise(resolve => {
            this.$emit('get-import-headers', resolve);
         });
      },

      // chartTypeChanged(val) {
      //    // TODO: only set if (val?.endsWith('Chart')). Otherwise, {}
      //    const options = {
      //       height: "250",
      //       legend: {
      //          position: "top"
      //       }
      //    };

      //    if (val === "DonutChart")
      //       options.pieHole = "0.3";

      //    if (val != "GeoChart") {
      //       options.chartArea = {
      //          left: "70",
      //          right: "20"
      //       };
      //    }

      //    this.chartOptions = sortedStringify(options);
      // },

      chartTypeChanged(val) {
         // TODO: only set if (val?.endsWith('Chart')). Otherwise, {}
         const options = {
            height: "250",
         };

         if (val === "GeoChart") {
            options.displayMode = 'markers';
         } else {
            options.legend = {
               position: "top"
            };

            options.chartArea = {
               left: "70",
               right: "20"
            };

            if (val === "DonutChart")
               options.pieHole = "0.3";
         }

         this.chartOptions = sortedStringify(options);
      },

      modelChanged(val) {
         this.isModelChanged = true;
         this.chartConfig.model = _jsonParse(val);
         //TODO: check idResolutions against items
         if (this.isDD) {
            this.autoSelectFirstItemDisabled = false;
         } else {
            let fldToFocus;
            if (this.chartConfig.tableType === 'other') {
               fldToFocus = 'chartType';
               this.getChartTypes();
            } else {
               fldToFocus = 'ptSelectedCols';
               // V260402
               const { sortColumn } = this.chartConfig;
               if (
                  this.chartConfig.tableType === 'CalculatedTable' &&
                  sortColumn &&
                  !this.chartConfig.model.metrics.map(m => m.label).includes(sortColumn)
               ) {
                  this.errMsg = `${sortColumn} is not a valid sort column anymore!`;
                  this.chartConfig.sortColumn = '';
               }
            }

            this.$refs.form.validate();
            this.$nextTick(() => {
               this.$refs[fldToFocus]?.focus();
            });
         }
      },

      getChartTypes() {
         const { type, metrics, pivotDimIn, dims, groupByDims } = this.chartConfig.model;
         this.chartTypes = [
            { text: 'Table', value: 'Table' }
         ];

         if (type === 'metrics') {
            this.chartTypes.push ({ text: 'KPI', value: 'KPI' });
         } else {
            this.chartTypes.push (...[
               { text: 'Area Chart', value: 'AreaChart' },
               { text: 'Bar Chart', value: 'BarChart' },
               { text: 'Column Chart', value: 'ColumnChart' },
               { text: 'Line Chart', value: 'LineChart' },
               { text: 'Pie Chart', value: 'PieChart' },
               { text: 'Donut Chart', value: 'DonutChart' }
            ]);

            if (metrics?.length === 1) {
               // V2603xx
               // // if (
               // //    type.endsWith('-metrics') ||
               // //    (type.endsWith('-dim') && pivotDimIn.length === 1)
               // // ) {
               // //    this.chartTypes.push (...[
               // //       { text: 'Pie Chart', value: 'PieChart' },
               // //       { text: 'Donut Chart', value: 'DonutChart' }
               // //    ]);
               // // }

               if (type.startsWith('dims-')) {
                  const dimKeys = dims || groupByDims;
                  const metricKeys = pivotDimIn || metrics.map(m => m.label);
                  if (metricKeys.length === 1 && _isValidGeoHierarchy(dimKeys)) {
                     this.chartTypes.push({ text: 'Geo Chart', value: 'GeoChart'});
                  }
               }
            }
         }
      },

      calculatedColumnsClicked() {
         this.ccErrMsg = '';
      },

      calculatedColumnsChanged(val) {
         const calculatedColumns = _jsonParse(val, []);
         if (this.chartConfig.chartType === 'CalculatedTable') {
            if (isEmpty(calculatedColumns)) {
               this.ccErrMsg = 'Value is required!';//Calculated Columns' cannot be empty! ";
            } else {
               this.chartConfig.calculatedColumns = calculatedColumns;
            }
         } else {
            this.chartConfig.calculatedColumns = calculatedColumns;
         }
      },

      // V260511
      idResolutionsChanged(val) {
         this.chartConfig.idResolutions = [..._jsonParse(val, [])];
      },

      saveChartConfig() {
         if (!this.$refs.form.validate()) return;
         // this.log(`in saveChartConfig(): chartConfig=${JSON.stringify(this.chartConfig)}`);
       
         const newChart = {};
         // this.chartConfig.options = _jsonParse(this.chartOptions);

         if (this.isFilter) {
            newChart.filterType = this.chartConfig.filterType;
            newChart.isTypeEditable = false;
            if (this.isDP) {
               newChart.dateColumn = this.chartConfig.dateColumn;
               newChart.defaultId = this.chartConfig.defaultId;
            } else {
               newChart.multiSelect = this.chartConfig.multiSelect;
            }
         } else {
            newChart.chartType = this.chartConfig.tableType === 'other'
               ? this.chartConfig.chartType : this.chartConfig.tableType;
            if (newChart.chartType === 'PaginatedTable') {
               newChart.ptSettings = this.chartConfig.ptSettings;//TODO: check for empty
               newChart.ptColumns = [
                  ...this.chartConfig.ptSelectedCols,
                  ...((this.chartConfig.ptOtherCols || '').split(',')
                     .map(col => col.trim()).filter(col => col))
               ];
            } else if (newChart.chartType === 'CalculatedTable') {
               const calculatedColumns = _jsonParse(this.chartCalculatedColumns);
               if (isEmpty(calculatedColumns)) {
                  this.ccErrMsg = 'Value is required!';//Calculated Columns' cannot be empty! ";
                  return;
               } else {
                  newChart.calculatedColumns = calculatedColumns;
               }

               if (this.chartConfig.columnsSequence.trim()) {
                  newChart.ctColumnsSequence = this.chartConfig.columnsSequence
                     .split(',').map(col => col.trim()).join(',');
               }

               if (this.chartConfig.sortColumn) {
                  newChart.sortColumn = this.chartConfig.sortColumn;
                  newChart.sortOrder = this.chartConfig.sortOrder;
               }
            // } else if (newChart.chartType === 'KPI') {   // V260305: added
            //    const calculatedColumns = _jsonParse(this.chartCalculatedColumns);
            //    if (!isEmpty(calculatedColumns)) {
            //       newChart.kpiCalculatedColumns = calculatedColumns;
            //    }
            //    newChart.metricsVisibility = this.chartConfig.metricsVisibility;
            //    if (newChart.metricsVisibility === 'C')
            //       newChart.visibleMetrics = this.chartConfig.visibleMetrics;
            // } else if (newChart.chartType !== 'KPI' && newChart.chartType !== 'Table') {
            } else if (newChart.chartType != 'Table') {
               const calculatedColumns = _jsonParse(this.chartCalculatedColumns, []);
               // if (!isEmpty(calculatedColumns)) {
               if (this.isPieChart || calculatedColumns.length) {
                  newChart.calculatedColumns = calculatedColumns;
                  newChart.metricsVisibility = this.chartConfig.metricsVisibility;
                  if (newChart.metricsVisibility === 'C')
                     newChart.visibleMetrics = this.chartConfig.visibleMetrics;
               }

               if (newChart.chartType != 'KPI') {
                  newChart.chartColors = this.chartConfig.chartColors;
                  newChart.options = this.chartConfig.options;
                  this.setChartColors(newChart, newChart.chartColors);
                  if (this.needLabels) {
                     newChart.chartLabels = this.chartConfig.chartLabels;
                  }
               }
            }
         }

         if (this.chartConfig.title)
            newChart.title = this.chartConfig.title;
         if (this.chartConfig.icon)
            newChart.icon = this.chartConfig.icon;
         if (this.chartModel)
            newChart.model = this.chartConfig.model;
         // V260511
         if (this.chartConfig.idResolutions?.length)
            newChart.idResolutions = [...this.chartConfig.idResolutions];

         this.$emit('input', newChart);
         this.$emit('chart-save');
         this.cancelChartConfig();
         if (this.isModelChanged && !this.isDP)
            this.$emit('model-change', this.chartConfig.model);

         // // TODO:this.chart = { ...newChart };
         // this.log('in saveChartConfig(): chart=' + sortedStringify(this.chart));
      },

      setChartColors(chart, colors) {
         const arrColors = (colors === 'global' ? this.globalChartColors : colors).split(',');
         if (chart.chartType === 'GeoChart') {
            if (!hasOwn(chart.options, 'colorAxis')) {
               chart.options.colorAxis = {};
            }
            chart.options.colorAxis.colors = arrColors;
         } else {
            chart.options.colors = arrColors;
         }
      },

      cancelChartConfig() {
         // _alert('in cancelChartConfig(): chartConfig=' + JSON.stringify(this.chartConfig));
         this.chartConfig = {};
         this.errMsg = '';
         this.dialogChartConfig = false;
         // // if (btnClicked)
         // //    this.chart = deepClone(this.originalChartConfig);
      },

      async createExportClicked() {
         this.loadingDialogExport = true;
         //TODO: this.exportFields = [...this.importHeaders, ...this.btHelpers.getLsExportFields()];
         this.exportFields = [];

         if (!this.lastExport) {
            const result = await this.apiService.getExports(this.lakeExportFilter, 1, 1);
            if (result.logout)
               this.logout();
            else if (result.message)
               return;

            // lakeYear, lakeMonth, 
            const { fieldDefinitions, debug, lakeColumnsCast, lakeDateFormats } = result.data;
            this.lastExport = {
               // lakeYear,
               // lakeMonth,
               fieldDefinitions,
               debug,
               columnsCast: lakeColumnsCast,
               dateFormat: lakeDateFormats?.dateformat || "",
               timestampFormat: lakeDateFormats?.timestampformat || ""
            };
         }

         // this.lastExport.lakeYear = ;
         // this.lastExport.lakeMonth = ;
         // const exportData = this.btHelpers.getLsExportData(this.lastExport, this.exportFields);
         const exportData = this.btHelpers.getExportData(this.lastExport, this.exportFields);
         const result = await this.apiService.createExport(exportData);
         this.loadingDialogExport = false;
         if (result.logout)
            this.logout();
         else if (!result.message) {
            // alert('result=' + JSON.stringify(result))
            this.$emit('snackbar-event', `'${exportData.name}' was submitted.`);
            this.cancelExport();
            this.lastExport = JSON.parse(JSON.stringify(this.exports[0]));
         }

         this.loadingDialogExport = false;
      },

      cancelExport() {
         this.dialogExport = false;
      },

      //TODO:
      async saveExport() {
         this.loadingDialogExport = true;

         // Removing $project from the standard filter as it is not needed for the PaginatedTable.
         const newFilter = JSON.parse(JSON.stringify(this.chart.filter.standard));
         const $projectInd = newFilter.findIndex(f => f.$project);
         if ($projectInd !== -1)
            newFilter.splice($projectInd, 1);

         this.lastExport.name = this.exportName;
         this.lastExport.aggregateQuery = JSON.stringify(newFilter); //this.chart.filter.standard
         // console.log('lastExport.aggregateQuery=' + this.lastExport.aggregateQuery);

         this.lastExport.fieldDefinitions = [];
         this.tableHeaders.forEach(header => {
            this.lastExport.fieldDefinitions.push({
               label: header.text,
               value: header.value,
               default: ''
            });
         });
         this.lastExport.actions = [];
         // this.log('in saveExport(): lastExport=' + JSON.stringify(this.lastExport));

         const exportData = this.btHelpers.getExportData(
            this.lastExport,
            this.exportFields
         );
         // this.log('exportData=' + JSON.stringify(exportData));

         const result = await this.csApiService.createExport(exportData);  //lastExport
         this.loadingDialogExport = false;
         if (result.logout)
            this.logout();
         else if (!result.message) {
            this.$emit('chart-export', `'${this.lastExport.name}' was created.`);
            this.exportName = '';
            this.cancelExport();
         }
      },

      dateChanged(val) {
         this.selectedDateValue = val;
      },

      async getPaginatedTableCount() {
         this.ptItemsCount = 0;
         this.chartData = [];

         let query = {
            type: 'metrics',
            metrics:[{ "metric":"count", "on":"DISTINCT _id", "label":"Count" }],
            whereClause: this.chart.model.whereClause,
            lake: this.chart.model.lake
         };

         const { data, hashVal } = this.getCachedData(query);
         if (data) {
            this.ptItemsCount = data;
         } else {
            const result = await this.lsApiService.getQueryResult(query);
            if (result.logout)
               this.logout();
            else if (result.message || !result.data?.length)
               this.ptItemsCount = 0;
            else {
               this.ptItemsCount = result.data[0].Count;
               this.$lsCache[hashVal] = this.ptItemsCount;
            }
         }
      },

      async getPaginatedTableData() {
         this.loadingChartData = true;
         this.chartData = [];
         const { model } = this.chart;
         const { page, itemsPerPage } = this.ptOptions;

         const limit = itemsPerPage || 5;
         const query = {
            ...model,
            limit: limit,
            offset: page ? (page - 1) * limit : 0,
         };

         let result;
         const { data, hashVal } = this.getCachedData(query);
         if (data) {
            result = { data };
         } else {
            result = await this.lsApiService.getQueryResult(query);
            if (result.logout)
               this.logout();
            else if (result.message || !result.data?.length)
               return;
            else {
               this.$lsCache[hashVal] = result.data;
            }
         }

         const ids = result.data.map(item => item._id);
         const filter = {
            standard: [{
               $match: {
                  _id: { $in: ids }
               }
            }],
            columns: this.chart.ptColumns
         };

         result = await this.csApiService.getOffers(filter, 1, ids.length); //, date
         this.loadingChartData = false;
         if (result.logout)
            this.logout();
         else if (result.message) { // || !result.data?.length
            this.chartData = [];
            return;
         }

         result.data.forEach(offer => {
            for (const key in offer) {
               if (key === '_id') continue;
               const val = offer[key] ? offer[key].toString().toLowerCase() : '';
               if (val === 'true' || val === 'false')
                  offer[key] = offer[key].toString();
            }
            offer._mfi_hasContactProfile = true;
         });

         this.chartData = [
            ...result.data,
            ...ids.filter(id => !result.data.some(cd => cd._id === id))
               .map(id => ({ _id: id }))
         ];


         if (this.isPtHeadersNeeded) { //TODO: set to true in model change
            this.ptHeaders = [];
            filter.columns.forEach(col => {
               this.ptHeaders.push({
                  //V260320: text: header?.text || col,
                  text: this.getPaginatedTableHeader(col),
                  value: col,
                  sortable: false   //header.isIndexed || false
               });
            });

            this.ptHeaders.push({
               text: '',
               value: 'actions',
               align: 'right',
               sortable: false
            });

            this.isPtHeadersNeeded = false;
            // _alert('in getOffers(): offerHeaders=' + JSON.stringify(this.offerHeaders));
         }
      },

      // V260320
      getPaginatedTableHeader(col) {
         if (this.effectiveLabels) {
            const label = this.effectiveLabels.find(lbl => lbl.id === col);
            if (label) return label.label;
         }

         const header = this.importHeaders.find(h => h.value === col);
         return header?.text || col;
      },

      async ptItemExpanded(items) {
         // _alert('in tableItemExpanded(): items=' + JSON.stringify(items));
         // _alert('in tableItemExpanded(): expanded=' + JSON.stringify(this.expanded));
         if (items.length === 0 || items[0].details)
            return;

         this.ptLoading = true;
         // await sleep(2000);
         const item = items[0];
         if (Object.keys(item).length === 1)
            item.details = [[{ key: '_id', value: `'${item._id}' has been deleted!` }], [], []];
         else {
            item.contactData = await this.btHelpers.getItem(item._id);
            if (item.contactData) {
               item.details = [[], [], []];
               let seq= 0;
               for (const key in item.contactData) {
                  item.details[seq++].push({ key: key, value: item.contactData[key] });
                  if (seq === 3) seq = 0;
               }
            }
         }

         this.ptLoading = false;
         // this.log('in tableItemExpanded(): item=' + JSON.stringify(item));
      },

      // V260511
      async getIdResolutionsData(idResolutions) {
         // alert(`in getIdResolutionsData(): idResolutions=${idResolutions?JSON.stringify(idResolutions):''}`)
         this.idResolutionsData = {};
         if (!idResolutions?.length) return;

         for (const idRes of idResolutions) {
            let { data, hashVal } = this.getCachedData(idRes.endpoint);

            if (!data) {
               data = idRes.systemDim
                  ? (await this.csApiService.getIdResolutionsData(idRes))?.data
                  : await this.fetchIdResolutionsData(idRes);

               if (data?.length)
                  this.$lsCache[hashVal] = data;
            }

            this.idResolutionsData[idRes.dim] = data;
         }
      },

      // V260511
      async fetchIdResolutionsData({ endpoint, id, name }) {
         try {
            const response = await fetch(endpoint, {
               method: 'GET',
               headers: {
                  // 'Authorization': `Bearer ${this.token}`,
                  'Content-Type': 'application/json'
               }
            });

            if (!response.ok)
               throw new Error(`HTTP error: ${response.status} ${response.statusText}`);

            const result = await response.json();
            return result.map(item => ({
               v: item[id],
               f: item[name]
            }));
         } catch (err) {
            const errMsg = `fetch failed for ${endpoint}: ${err.message}`;
            console.error(errMsg);
            alert(`Exception occurred in fetchIdResolutionsData() while fetching data from ${endpoint}: ${errMsg}`);
            return [];
         }
      },

      async getChartData(query, chartType) {
         // this.log(`in getChartData(): query=${JSON.stringify(query)}\nchartType=${chartType}}`);
         this.queryResult = [];
         this.queryResultHeaders = [];
         this.chartData = [];
         if (!this.lastChartType)
            this.lastChartType = chartType;
         
         let result;
         const { data, hashVal } = this.getCachedData(query);
         if (data) {
            result = { data };
         } else {
            result = await this.lsApiService.getQueryResult(query);
            if (result.logout)
               this.logout();
            else if (result.message || !result.data?.length)
               return;
            else {
               // V260520
               if (query.type === MODELS.histogram &&
                  this.schemas.find(s => s.column_name === query.histogramOn)?.column_type.startsWith('DECIMAL')
               ) {
                  result.data = this.formatChartData(result.data, `${query.histogramOn}_Range`);
               }

               this.$lsCache[hashVal] = result.data;
            }
         }

         if (query.type === MODELS.column) {
            // alert('result.data=' + JSON.stringify(result.data))

            /* V260512 - start: Applied idResolutions for dropdowns */
            // this.chartData = result.data.map(obj => Object.values(obj)[0]).sort();
            const key = Object.keys(result.data[0])[0];
            const resolutions = this.idResolutionsData[key]; // undefined if not resolvable
            this.chartData = result.data.map(obj => {
               const value = obj[key];
               const match = resolutions?.find(r => String(r.v) === String(value));
               return {
                  value,
                  text: match ? match.f : value
               };
            }).sort((a, b) => a.text.localeCompare(b.text));
            /* V260512 - end */

            return;
         }

         // let orderedQueryKeys;
         let addLabels;
         if (query.type === MODELS.dateDate) {
            this.queryResult = this.reorderDateDateResult(query, result.data);
            // orderedQueryKeys = Object.keys(this.queryResult[0]);
            this.modelDims = Object.keys(this.queryResult[0]);
            addLabels = false;
         } else {
            this.queryResult = deepClone(result.data);
            // const labels = query.metrics.map(m => m.label);
            this.modelLabels = query.metrics.map(m => m.label);
            switch (query.type) {
               case MODELS.metrics:
                  // orderedQueryKeys = labels;
                  this.modelDims = [];
                  addLabels = true;
                  break;
               case MODELS.dims: //CalculatedTable
                  //V260128.2: this.modelDims = [...this.getDims(query.dims)];
                  this.modelDims = query.dims.map(d => {
                     const parts = d.split(' as ');
                        return parts[parts.length - 1];
                  });
                  addLabels = true;
                  break;
               case MODELS.dimsDim:
                  // orderedQueryKeys = [...query.groupByDims, ...query.pivotDimIn];
                  this.modelDims = [...query.groupByDims, ...query.pivotDimIn];
                  addLabels = false;
                  break;
               case MODELS.date: //CalculatedTable
                  // orderedQueryKeys = [...query.dateParts, ...labels];
                  this.modelDims = [...query.dateParts];
                  addLabels = true;
                  break;
               case MODELS.dateDim:
                  // orderedQueryKeys = [...query.dateParts, ...query.pivotDimIn];
                  this.modelDims = [...query.dateParts, ...query.pivotDimIn];
                  addLabels = false;
                  break;
               case MODELS.histogram:  //CalculatedTable
                  this.modelDims = [query.histogramLable];
                  addLabels = true;
                  break;
               // default:
               //    orderedQueryKeys = Object.keys(result.data[0]);
               //    break;
               default:
                  alert(`Error in getChartData(): Unsupported model: ${query.type}`);
                  break;
            }
         }

         if (chartType === 'CalculatedTable')
            this.chartData = deepClone(result.data);  // V260305: Deserialized for loading bug
         else if (chartType != 'KPI') {
            this.prepareChartData(query, chartType);
            if (this.chartData.length) {
               /* V260108 & V260320: start */
               if (this.effectiveLabels) {
                  if (this.chart.model.pivotDim === 'event_code') {
                     for (let index = 1; index < this.chartData[0].length; index++) {
                        const val = this.chartData[0][index];
                        const lbl = this.effectiveLabels.find(l => l.id === val);
                        if (lbl)
                           this.chartData[0][index] = lbl.label;
                     }
                  } else {
                     // const eventCodeInd = this.chart.model.groupByDims.findIndex(dim => dim === 'event_code');
                     const dimsArr = this.chart.model.groupByDims || this.chart.model.dims || [];
                     const eventCodeInd = dimsArr.indexOf('event_code');
                     if (eventCodeInd > -1) {
                        for (let index = 1; index < this.chartData.length; index++) {
                           const val = this.chartData[index][0];
                           if (val) {
                              const valParts = val.split(":");
                              const lbl = this.effectiveLabels.find(l => l.id === valParts[eventCodeInd]);
                              if (lbl) {
                                 valParts[eventCodeInd] = lbl.label;
                                 this.chartData[index][0] = valParts.join(':');
                              }
                           }
                        }
                     }
                  }
               }
               /* V260108 & V260320: end */
            }
         }

         const orderedQueryKeys = [
            ...this.modelDims,
            ...(addLabels ? this.modelLabels : [])
         ];

         this.queryResultHeaders = orderedQueryKeys?.map(key => ({
            text: key,
            value: key,
            align: 'left',
            sortable: true
         }));

         // this.log('in getChartData(): queryResultHeaders=' + JSON.stringify(this.queryResultHeaders))
      },

      // V260520
      formatChartData(data, rangeKey) {
         const formattedData = data.map(d => {
            const val = d[rangeKey]?.trim() || '';

            let formattedVal;
            if (val.startsWith('<') || val.startsWith('>')) {
               const sign = val.substring(0, 1);
               const num = val.substring(1).trim();
               formattedVal = `${sign} ${_to2Decimals(num)}`;
            } else if (val.includes('-')) {
               const parts = val.split('-').map(p => p.trim());
               formattedVal = `${_to2Decimals(parts[0])}-${_to2Decimals(parts[1])}`;
            } else {
               formattedVal = val;
               alert(`${val} could not be formatted!`);
            }

            // Destructure to separate the old range value from the rest of the object
            const { [rangeKey]: oldRange, ...rest } = d;

            return {
               [rangeKey]: formattedVal,
               ...rest
            };
         });

         return formattedData;
      },

      reorderDateDateResult(query, data) {
         // this.log(`in reorderDateDateResult(): query=${JSON.stringify(query)}\ndata=${JSON.stringify(data)}`);
         // const prefix = toPascalCase(this.modelData.dateParts[1]) + ' ';
         const prefix = query.dateParts[1] + ' ';
         const renamedData = data.map(obj => {
            const newObj = {};
            Object.entries(obj).forEach(([key, value]) => {
               if (!isNaN(key)) {
                  newObj[`${prefix}${key}`] = value;
               } else {
                  newObj[key] = value;
               }
            });

            return newObj;
         });

         const result = [];
         const keys = Object.keys(renamedData[0]);
         const lastKey = keys[keys.length - 1];
         deepClone(renamedData).forEach(item => {
            const orderedMap = new Map();
            orderedMap.set(lastKey, item[lastKey]);
            for (let i = 0; i < keys.length - 1; i++) {
               orderedMap.set(keys[i], item[keys[i]]);
            }
            result.push(Object.fromEntries(orderedMap));
         });

         return result;
      },

      async prepareChartData(query, chartType) {
         let dimKeys, metricKeys;
         switch (query.type) {
            case MODELS.dims:
               dimKeys = this.modelDims;        //this.getDims(query.dims);
               metricKeys = this.modelLabels;   //query.metrics.map(m => m.label);
               break;
            case MODELS.dimsDim:
               dimKeys = query.groupByDims;
               metricKeys = query.pivotDimIn;
               break;
            case MODELS.date:
               dimKeys = this.modelDims;        //query.dateParts;
               metricKeys = this.modelLabels;   //query.metrics.map(m => m.label);
               break;
            case MODELS.dateDate:
               this.chartData = this.prepareChartData4DateDate(query);
               return;
            case MODELS.dateDim:
               dimKeys = query.dateParts;
               metricKeys = query.pivotDimIn;
               break;
            case MODELS.histogram:
               dimKeys = this.modelDims;        //[query.histogramLable];
               metricKeys = this.modelLabels;   //query.metrics.map(m => m.label);
               break;
            default:
               alert(`Error in prepareChartData(): Unsupported model: ${query.type}`);
               break;
         }

         if (chartType === 'GeoChart') {
            this.chartData = this.prepareGeoChartData(dimKeys, metricKeys[0]);
         } else {
            // V260305: lots of changes to inject calculated columns
            const visibleMetrics = this.getVisibleMetrics();
            // NOTE: visibleMetrics can be a string or an Array
            const availMetricKeys = metricKeys.filter(k => [].concat(visibleMetrics).includes(k));
            const calColKeys = this.isPieChart && availMetricKeys.length
               ? []
               : this.calCols?.map(c => c.name) || [];
            const header = [
               'Label',
               // ...metricKeys,
               // ...(this.calCols?.map(c => c.name) || [])
               ...availMetricKeys,
               ...calColKeys
            ];
            
            const rows = this.queryResult.map(item => {
               // alert('item=' + JSON.stringify(item))
               
               /* V260512 - start: Applied idResolutions for charts */
               // const label = dimKeys.map(key => item[key]).join(':');
               const vParts = [];
               const fParts = [];
               dimKeys.forEach(key => {
                  const rawVal = item[key];
                  vParts.push(rawVal);
                  if (this.idResolutionsData[key]) {
                     const match = this.idResolutionsData[key].find(r => String(r.v) === String(rawVal));
                     fParts.push(match ? match.f : rawVal);
                  } else {
                     fParts.push(rawVal);
                  }
               });
               /* V260512 - end */

               const metrics = availMetricKeys.map(key => item[key] ?? 0);
               return [
                  //V260512: label,
                  { v: vParts.join(':'), f: fParts.join(':') },
                  ...metrics,
                  // ...(this.calCols.length ? this.getCalculatedValues(item, this.calCols) : [])
                  ...(calColKeys.length ? this.getCalculatedValues(item, this.calCols) : [])
               ];
            });

            this.chartData = [header, ...rows];
            // this.log('in prepareChartData(): chartData=' + JSON.stringify(this.chartData));
         }
      },

      // V260305
      getCalculatedValues(item, calColumns) {
         const calVals = [];
         const itemKeys = Object.keys(item);
         calColumns?.forEach(calCol => {
            let resolvedExpr = calCol.expression;
            calCol.vars.forEach(v => {
               if (itemKeys.includes(v)) {
                  resolvedExpr = resolvedExpr.replace('{{' + v + '}}', item[v]);
               }
            });
            calVals.push(eval(resolvedExpr));
         });

         // alert('calVals=' + JSON.stringify(calVals));
         return calVals;
      },

      prepareChartData4DateDate(query) {
         const xKey = query.dateParts[0];
         const header = Object.keys(this.queryResult[0]);
         const seriesKeys = header.filter(key => key !== xKey);
         const rows = this.queryResult.map(item => {
            const xVal = String(item[xKey]);
            const values = seriesKeys.map(key => item[key] ?? 0);
            return [xVal, ...values];
         });

         return [header, ...rows];
      },

      prepareGeoChartData(dimKeys, metricKey) {
         let chartData = [["Location", metricKey]];
         for (const row of this.queryResult) {
            const location = dimKeys.map(field => row[field]).filter(Boolean).join(", ");  // was '/' then '-'
            chartData.push([location, row[metricKey]]);
         }
         return chartData;
      },

      // V260128.2
      // getDims(dims) {
      //    return dims.map(d => {
      //       const parts = d.split(' as ');
      //       return parts[parts.length - 1];
      //    });
      // },

      formatNumber(val, key) {
         if (typeof val === 'string' || ['year', 'quarter', 'month', 'day', 'hour'].includes(key))
            return val;
         else
            return formatNumber(val, 2);
      },

      async getPivotDimInItems(pivotDim) {
         const query = {
            type: MODELS.column,
            column: pivotDim,
            whereClause: '',
            lake: this.lake
         };

         const { data, hashVal } = this.getCachedData(query);
         if (data) {
            this.pivotDimInItems = data;
         } else {
            let result = await this.lsApiService.getQueryResult(query);
            if (result.logout) this.logout();
            else if (!result.message && result.data?.length) {
               this.pivotDimInItems = result.data.map(obj => Object.values(obj)[0]).sort();
               this.$lsCache[hashVal] = this.pivotDimInItems;
            } else this.pivotDimInItems = [];
         }
      },

      getSelectColumnsAndData(values, dimKeys, dateField, histogramColumn, resolveLabel) {
         // alert('values='+JSON.stringify(values) + '\n' +
         // 'dimKeys='+JSON.stringify(dimKeys) + '\n' +
         // 'dateField='+dateField + '\n' +
         // 'histogramColumn='+histogramColumn);
         const selectColumns = [];
         const selectData = [];
         if (histogramColumn) {
            selectColumns.push(histogramColumn);
            const val = values[0].trim();
            if (val.startsWith('<')) {
               selectData.push(`${histogramColumn} < ${Number(val.replace('<', '').trim())}`);
            } else if (val.startsWith('>')) {
               //V260518: selectData.push(`${histogramColumn} > ${Number(val.replace('>', '').trim())}`);
               selectData.push(`${histogramColumn} >= ${Number(val.replace('>', '').trim())}`);
            } else if (val.includes('-')) {
               const [min, max] = val.split('-').map(v => Number(v.trim()));
               //V260518: selectData.push(`${histogramColumn} between ${min} and ${max}`);
               selectData.push(`${histogramColumn} >= ${min} and ${histogramColumn} < ${max}`);
            }
         } else {
            for (let i = 0; i < dimKeys.length; i++) {
               const key = dimKeys[i];
               const val = values[i];
               if (dateField) {
                  selectColumns.push(dateField);
                  selectData.push(`${key}(${dateField}) = ${Number(val)}`);
               } else {
                  selectColumns.push(key);
                  let id;
                  // V260320
                  if (key === 'event_code' && this.effectiveLabels) {
                     id = this.effectiveLabels.find(l => l.label === val)?.id;
                  }
                  // selectData.push(`${key} = '${values[i]}'`);
                  selectData.push(`${key} = '${id || val}'`);
               }
            }
         }

         return { selectColumns, selectData };
      },

      // V260223: new
      buildClickData(row, dims) {
         // alert(`in ${NAME}.buildClickData(): row=${JSON.stringify(row)}\ndims=${JSON.stringify(dims)}`);

         const { dateField, histogramOn } = this.chart.model;

         if (dateField || histogramOn) {
            const { selectColumns, selectData } = this.getSelectColumnsAndData(
               dims.map(key => row[key]),
               dims,
               dateField,
               histogramOn
            );

            return {
               columns: selectColumns,
               data: selectData
            }
         }

         const selectData = [];
         dims.forEach(dim => {
            selectData.push(`${dim} = '${row[dim]}'`);
         });

         return {
            columns: dims,
            data: selectData
         }
      },

      // V260223: renamed and part of the code moved to buildClickData().
      tableRowClicked(row, ind, dims) {
         // alert(`in ${NAME}.tableRowClicked(): row=${JSON.stringify(row)}\nind=${ind}\ndims=${JSON.stringify(dims)}\ncolName=${colName}`);
         if (this.isEditing) return;

         this.clickedRowInd = this.clickedRowInd === ind ? -1 : ind;

         if (this.clickedRowInd === -1) {
            this.$emit('chart-reclick');
            return;
         }

         const clickData = this.buildClickData(row, dims);
         this.$emit('chart-click', clickData);
      },

      // V260224: new
      ctReady() {
         this.loadingChartData = false;
      },

      // V260223: new
      ctCellClicked(row, ind, dims, colName) {
         // alert(`in ${NAME}.ctCellClicked(): row=${JSON.stringify(row)}\nind=${ind}\ndims=${JSON.stringify(dims)}\ncolName=${colName}`);
         if (this.isEditing) return;

         this.clickedRowInd = this.clickedRowInd === ind ? -1 : ind;

         const clickData = this.buildClickData(row, dims);  //, colName

         const metricFilter = this.chart.model.metrics.find(metric => metric.label === colName);
         if (metricFilter) {
            clickData.filter = metricFilter.filter;
            clickData.exclude = metricFilter.exclude;
         }

         // alert('clickData=' + JSON.stringify(clickData))
         this.$emit('chart-click', clickData);
      },

      // V260223: new
      ctCellReclicked() {
         this.$emit('chart-reclick');
      },

      // V2603xx: new
      kpiMetricReclicked() {
         this.$emit('chart-reclick');
      },

      // V260226: new
      getCachedData(query) {
         const cache = {
            hashVal: md5Hash(this.aid + JSON.stringify(query))
         }

         if (this.$lsCache && this.$lsCache[cache.hashVal]) {
            cache.data = this.$lsCache[cache.hashVal];
         }

         // console.warn('cache=' + JSON.stringify(cache) + '\nquery=' + JSON.stringify(query));
         return cache;
      },

      // V260305
      getVisibleMetrics() {
         if (!this.chart.metricsVisibility || this.chart.metricsVisibility === 'A')
            return this.chart.model.metrics.map(m => m.label);
         if (this.chart.metricsVisibility === 'N')
            return [];
         if (this.chart.metricsVisibility === 'C')
            return this.chart.visibleMetrics;
      }
   },

   created() {
      // alert('in created');
      if (this.isFilter) {
         this.filterTypes = [
            { text: 'Date Picker', value: 'DatePicker' }
         ];

         if (this.filterIndex > 0)
            this.filterTypes.push({ text: 'Dropdown', value: 'Dropdown' });
      }
   },

   mounted() {
      // alert(JSON.stringify(this.schemas));
   }
}
</script>

<style scoped>
::v-deep .v-text-field__details {
   margin-bottom: 0px;
}
.v-input__slot {
   margin: 0;
}
.v-text-field {
    padding-top: 0px;
    margin-top: 0px;
}
.clicked-row {
   background-color: #e3f2fd !important;  /* rgba(33, 150, 243, 0.15); light blue */
   transition: background-color 0.2s;
}
.pdf-flex {
   display: flex !important;
   justify-content: space-between !important;
   width: 100% !important;
}
</style>
